import type { CourseAudience, CourseNeed, CourseTopic } from "./topics";

/**
 * ScenarioSpec — the framework-agnostic contract for an Interactive 3D training.
 *
 * This JSON is the single source of truth a scenario. It is authored by the
 * "AI Studio" pipeline (Producer + Art Director + World/Asset agents) and consumed
 * by the runtime. It is deliberately decoupled from React/Three so it can later
 * become a Cyberfrog `interactive_3d` training kind with zero runtime rewrite:
 *   - device shells read `devices`
 *   - the camera reads `scene.waypoints`
 *   - the state machine reads `beats` + `edges`
 *   - the voice attacker reuses the same shape as packages/shared/services/voice
 *   - scoring/conditions read ONLY `journeyOutcomes`-style events (never raw UI)
 *
 * Nothing here references hosting: every asset URL is a path our own VPS serves
 * (e.g. `/cdn/sim/<scenario>/office.spz`). Generation happens at authoring time.
 */

// ---------------------------------------------------------------------------
// Environment (self-hosted, device-tiered)
// ---------------------------------------------------------------------------

export type DeviceTier = "high" | "mid" | "low";

/** A photoreal environment with per-tier LOD variants so thousands of varied
 *  learner devices each pull the right payload. All URLs are our own /cdn. */
/** Daylight exterior settings (see EnvironmentSpec.outdoor). */
export interface OutdoorSpec {
  /** Sun height above the horizon. Low = warm and long-shadowed. Default 40. */
  sunElevationDeg?: number;
  /** Sun compass bearing, controls which way shadows fall. Default 145. */
  sunAzimuthDeg?: number;
  /** Atmospheric haze. 2 = crisp alpine, 10 = hot and hazy. Default 4. */
  turbidity?: number;
  /** Blue-scattering strength; lower washes the sky paler. Default 2. */
  rayleigh?: number;
  /** Sun intensity for the directional light. Default 2.4. */
  sunIntensity?: number;
  /** Cast real-time sun shadows. Only for models with no baked lighting. */
  shadows?: boolean;
  /** Radius (metres) the shadow camera covers around the origin. Default 26. */
  shadowRadius?: number;
  /** Ground plane stretching to the horizon beneath the model. */
  ground?: {
    /** Fallback tint when no pack albedo is supplied. */
    color?: string;
    /** Edge length in metres. Default 90. */
    size?: number;
    /** Height, so the model's own slabs sit on top. Default -0.04. */
    y?: number;
    /** Paid-pack albedo URL (tiled). Prefer this over a generated grass. */
    textureUrl?: string;
    /** How many times the albedo repeats across the plane. Default 22. */
    textureRepeat?: number;
  };
  /** Instanced palm belt on the far/side horizon. Cheap billboards, not GLB trees. */
  palms?: {
    count?: number;
    radius?: number;
    /** Extra short palms at [x, z] — typically sitting in pack planters. */
    pots?: [number, number][];
  };
}

/** Animated water surface (see EnvironmentSpec.water). */
export interface WaterSpec {
  /** Surface centre in world space; y is the waterline. */
  center: [number, number, number];
  /** Extent along world X, or diameter when shape is circle. */
  width: number;
  /** Extent along world Z. Ignored for circles. */
  depth: number;
  /** Deep-water tint. Default a pool cyan. */
  color?: string;
  /** Ripple strength, mapped onto the normal scale. Default 0.55. */
  distortionScale?: number;
  /** Unused by the physical-water path; kept so older specs still type-check. */
  alpha?: number;
  /** Ripple speed multiplier. Default 1. */
  speed?: number;
  /** Pool-floor plane under the water, so lane tiles read through. */
  floorY?: number;
  /** Rectangular lap pool (default) or circular jacuzzi / kids pool. */
  shape?: "rect" | "circle";
}

export interface EnvironmentSpec {
  /** Gaussian-splat world (World Labs Marble export), per tier. */
  splat: {
    /** ~2M splats, WebGPU/high-end desktops. */
    high?: AssetRef;
    /** ~500k splats, WebGL2 / mid laptops. */
    mid?: AssetRef;
    /** Baked 360deg pano or pre-rendered backdrop for low-end / no-GPU. */
    low?: AssetRef;
  };
  /** Optional collider mesh for raycasting/placement (Marble collider GLB). */
  colliderMesh?: AssetRef;
  /** HDRI / lighting environment map for PBR props. */
  hdri?: AssetRef;
  /** Fallback solid/gradient backdrop colour if nothing loads. */
  backdropColor?: string;
  /** Tone-mapping exposure override for this environment (pano/splat only). Marble
   *  panos vary a lot in brightness; a dark one (e.g. the apartment) needs a higher
   *  value so the objects read. Defaults to 1.35 (pano) / 1.15 (splat) / 1.05. */
  exposure?: number;
  /** Procedural environment to render (zero external assets). This is the base
   *  scene and the splat fallback. Defaults to "office". */
  procedural?:
    | "office"
    | "hotel_suite"
    | "office_social"
    | "restaurant"
    | "hacker_room"
    | "living_room"
    | "apartment"
    | "train_carriage"
    | "train_platform"
    | "walkable_office"
    // Not a real renderer — office-under-attack uses a purchased gltfUrl room
    // (which always wins over `procedural` in Environment3D), so this value
    // is inert for 3D rendering. It exists solely to stamp `data-env="oua_desk"`
    // on .sim-root so styles.css can scope this course's own --scr-w/--scr-h
    // screen-overlay sizing + "click the screen" sticky-note hint.
    | "oua_desk"
    // Same stamp-only trick for pool-walkable (gltfUrl wins). Scopes the
    // iPad overlay --scr-* box after the opening camera zoom.
    | "pool_walkable"
    // Purchased Cozy Beach Bar GLB; stamp scopes Holiday Call overlays.
    | "holiday_beach"
    // Stamp only. Send It Safely still renders the office room; this value
    // exists so the monitor can be framed larger than the shared 62vh office.
    | "sendit_desk";
  /** Opt-in: attempt the Marble Gaussian-splat world. OFF by default because the
   *  splat path is a work-in-progress; until it's solid every scenario uses its
   *  procedural environment, which always renders. */
  enableSplat?: boolean;
  /**
   * Prefer the Marble equirectangular pano (photoreal, camera locked at centre)
   * instead of the .spz. Use when splat yaw/floor is not yet proven — learners
   * cannot dolly under the floor or outside walls.
   */
  enablePano?: boolean;
  /** Explicit pano URL; default = sibling pano.jpg next to the mid/high .spz. */
  panoUrl?: string;
  /** Yaw degrees for enablePano framing. */
  panoRotationYDeg?: number;
  /** Flip the equirect vertically (Marble worlds that load upside-down). */
  panoFlipVertical?: boolean;
  /** Yaw offset (deg) that aligns equirect-anchored pack markers with this pano.
   *  Decoupled from panoRotationYDeg (which rotates the room mesh). Calibrated
   *  once per world against one known object; `?pinYaw=` overrides live. */
  packPinYawOffsetDeg?: number;
  /** Restrict pano look-around to +/- this many degrees from the initial camera
   *  waypoint's forward direction. Use when the source only shows a real scene
   *  (e.g. seated passengers) on one side and the rest is empty/unrendered. */
  panoYawLimitDeg?: number;
  /**
   * When true, beats with `hold: true` keep OrbitControls look-around on
   * (Marble pano spin-in-place), matching remote-work pin/explore rooms.
   * Opt-in per environment so other courses stay choreographed.
   */
  lookAround?: boolean;
  /** Per-world alignment for the splat (Marble exports vary in origin/scale/up).
   *  Applied to the SplatMesh so its floor sits near y=0 and the area the camera
   *  waypoints look at lines up with the world. */
  splatTransform?: SplatTransform;
  /**
   * Purchased/real mesh room: a self-hosted glTF/GLB with actual walkable
   * geometry (not a single-view splat reconstruction). When set, this wins
   * over `procedural` (procedural is legacy Lego-style geometry). Waypoints
   * and colliders are authored directly in the model's own coordinate space
   * (no auto-fit) — measure once via the analyzer + `?free=1`, bake it in.
   */
  gltfUrl?: string;
  /** Uniform scale applied to the loaded glTF scene (default 1). */
  gltfScale?: number;
  /** Translation applied to the loaded glTF scene AFTER scale (default origin). */
  gltfPosition?: [number, number, number];
  /** Euler rotation (degrees, XYZ) applied to the loaded glTF scene (default none). */
  gltfRotationDeg?: [number, number, number];
  /** Optional real-mesh AABB colliders for this glTF room (world-space, post-transform). */
  gltfColliders?: { min: [number, number, number]; max: [number, number, number] }[];
  /**
   * Opt-in daylight exterior: physical sky + a real sun that drives both the
   * directional light and the image-based lighting.
   *
   * ONLY for models whose textures carry NO baked lighting (the pool pack is raw
   * PBR). A model with lighting already painted into its albedo — office-walkable
   * — must leave this unset or it double-lights and blows out. Absent by default,
   * so every existing course keeps the current indoor profile untouched.
   */
  outdoor?: OutdoorSpec;
  /**
   * Opt-in animated water surface (reflection + refraction + rippling normals),
   * as a horizontal rectangle in world space. Sits inside a modelled basin; the
   * model supplies the walls and floor under it.
   */
  water?: WaterSpec;
  /** Extra basins (jacuzzi / kids pool) using the same water shader. */
  waters?: WaterSpec[];
  /** Add the course-local sand, palms, horizon dressing and physical table/iPad
   * around the small purchased Cozy Beach Bar shell. */
  beachSurround?: boolean;
  /** Canvas shown on Elena's physical bar iPad before the HTML overlay takes over. */
  beachIpad?: "teams" | "home" | "notify";
  /** beachIpad "notify": lock-screen notification drawn on the 3D iPad. */
  beachIpadNotice?: { time: string; app: string; from: string; text: string };
  /** Drink on the beach counter. Default "cup". */
  beachDrink?: "cup" | "cocktail";
  /** A phone lying on a desk/table; where it lies follows Beat.mobileAt. */
  deskMobile?: DeskMobileSpec;
  /** Optional procedural-room knobs (e.g. restaurant sign text). */
  props?: { signText?: string; signSub?: string; [k: string]: unknown };
  /**
   * Optional interactive workstation placed on a desk in a walkable glTF room:
   * a procedural PC monitor + keyboard with a yellow sticky note stuck on the
   * bezel. The sticky note is a clickable hotspot (projected to screen each
   * frame). Clicking it zooms into a 2D full-screen view of the PC login screen
   * with the password from the sticky note — a "don't write passwords on sticky
   * notes" awareness moment.
   */
  deskPc?: DeskPcSpec;
  /**
   * Optional wall-mounted conference-room TV. It is a "switch on the TV" hotspot
   * (projected to screen each frame like the desk PC); once switched on, an
   * emissive image plane lights up over the panel showing `imageUrl`. Used for
   * the storyboard's second task (go to the conference room, switch on the TV).
   */
  conferenceTv?: ConferenceTvSpec;
  /**
   * Optional physical welcome-kiosk terminal placed as a 3D object on a desk
   * or counter surface in a walkable glTF room (e.g. an unattended reception
   * counter). Renders as a real modelled kiosk (base + neck + tilted screen),
   * not a DOM overlay. It reports a screen-projected pin each frame like
   * `deskPc`/`conferenceTv` so App can show a "switch on" hotspot when the
   * learner is near it.
   */
  receptionTerminal?: ReceptionTerminalSpec;
  /**
   * Optional wall-mounted entry intercom / video door station near the secured
   * entrance. A physical 3D panel (screen + speaker grille + call button) the
   * camera can push in on to start the visitor (tailgate) decision, instead of a
   * CCTV card appearing from nowhere. Like receptionTerminal it is NOT gated on
   * `scenario.walkable`. Position/rotation are live-tunable via
   * `?intercomPos=x,y,z` / `?intercomRot=deg` for wall calibration.
   */
  entryIntercom?: EntryIntercomSpec;
  /**
   * Optional physical package sitting on the SAME desk as `deskPc` (e.g. an
   * unexpected delivery with a labelled USB drive inside). Renders as a real
   * modelled cardboard box + a yellow "Click to open" sticky note, not a 2D
   * card floating over the scene. Like `receptionTerminal`/`entryIntercom` it
   * is NOT gated on `scenario.walkable` — it is a real 3D prop that reports a
   * screen-projected pin each frame so App can show an "open" hotspot.
   */
  deskPackage?: DeskPackageSpec;
  /**
   * Optional set of real 3D "document" props scattered around this room for
   * the Episode-3 "office privacy" pair (find them walking around in 3D,
   * then sort them in the drag-and-drop game). Like `receptionTerminal`/
   * `entryIntercom`/`deskPackage` these are real modelled props (small
   * standing cards), NOT gated on `scenario.walkable` — they report a
   * screen-projected pin per item every frame.
   */
  privacyRoom?: PrivacyRoomSpec;
  /**
   * Optional physical desk phone sitting on the reception counter (Episode 13
   * live vishing call). Renders as a real modelled desk phone (base + handset
   * + keypad) that PULSES with an emissive ring + a glow halo while `ringing`
   * is true (driven from App by the active beat). Not gated on
   * `scenario.walkable`, like the reception terminal / intercom / package.
   */
  deskPhone?: DeskPhoneSpec;
  /**
   * Optional framed wall posters (funny illustrations + short slogans) baked
   * as real modelled props (illustration+caption texture on a plane, thin
   * extruded frame) — not gated on `scenario.walkable`. Exactly one entry may
   * set `linkUrl`; that poster reports a screen-projected click pin each
   * frame like `deskPackage`/`conferenceTv` so App can open the link.
   */
  officePosters?: OfficePosterSpec[];
  /**
   * Optional plain blocking wall panels (a real prop, not a decal) — used
   * exactly once so far: office-under-attack's reception counter wall
   * (`04_Wall_Reception`) doesn't span the full room width, leaving two
   * open gaps beside it straight into the unused back room where the wall
   * posters live. A camera glimpsing through either gap (verified live from
   * the Episode-3 free-look camera) would defeat "not visible from the
   * front desk", so these physically seal the gaps rather than relying on
   * hoping no camera angle ever lines up with them. `position` is the box
   * CENTER, `size` is full width/height/depth (not half-extents).
   */
  wallInfills?: { position: [number, number, number]; size: [number, number, number]; color?: string }[];
  /**
   * Optional wall-mounted screens that play a LOOPING MP4 (not a static image
   * like `conferenceTv`) — e.g. a "TV always on in the background" playing a
   * news/sports program for atmosphere. Purely decorative: always on, no
   * on/off hotspot, not gated on `scenario.walkable`. Renders a video-textured
   * plane flush over the panel, with a subtle CRT-style flicker.
   */
  videoScreens?: VideoScreenSpec[];
  /**
   * Optional procedural open laptop prop sitting on a counter/desk surface —
   * purely decorative atmosphere prop (not an interactive device).
   */
  laptops?: LaptopPropSpec[];
  /**
   * Optional procedural vertical QR-code standee (like a table-tent card)
   * sitting on a counter/table surface — purely decorative for now; a future
   * course can add a click/zoom hotspot on it for a scan-the-QR mini-game.
   */
  qrStands?: QrStandSpec[];
  /**
   * Optional static purchased/authored glTF props (e.g. a scanned human
   * figure) dropped into the scene at a fixed transform — purely decorative,
   * no animation/rig driven here. Distinct from `gltfUrl` (the ONE big room
   * mesh): this is for small supplementary assets layered on top of it.
   */
  staticGltfProps?: StaticGltfPropSpec[];
  /**
   * Optional invisible world-space click prompt for a walkable free-roam beat
   * (e.g. "Order a drink" near the bartender, ending free explore). Reports a
   * screen-projected pin each frame like `deskPackage`/`officePosters` — but
   * has NO visible 3D geometry of its own (the room's real props already read
   * fine; this only anchors WHERE the DOM prompt/label belongs).
   */
  counterHotspot?: CounterHotspotSpec;
  /**
   * Optional world-space screen rectangle for a real 3D monitor in this room
   * (e.g. the hacker's CRT). When set, App mounts a device DOM overlay
   * (MitmReveal) inside the screen box that `GltfScreenOverlay` projects each
   * frame, so the overlay stays glued to the monitor at any viewport size —
   * replacing hand-tuned vw/vh CSS. Calibrate live via
   * `?screenQuad=cx,cy,cz,w,h,yaw[,tilt]`.
   */
  screenQuad?: ScreenQuadSpec;
  /** Walkable arcade hub: the playable cabinets and the briefing TV. */
  arcadeSpots?: ArcadeSpotSpec[];
  /** Wall TV mesh drawn by ArcadeMarkers (its picture is DOM, via a beat screenQuad). */
  arcadeTv?: ArcadeTvSpec;
}

export interface CounterHotspotSpec {
  /** World-space point the prompt is anchored to (e.g. just above the bar counter). */
  position: [number, number, number];
  /** Label shown next to the pin (e.g. "Order a drink"). */
  label: string;
  /** Max distance (metres) at which the pin is considered clickable/visible (default 6). */
  maxDistance?: number;
}

/**
 * A world-space rectangle standing in for a 3D screen/monitor surface, used by
 * `GltfScreenOverlay` to project the screen to viewport coordinates every frame
 * so a DOM overlay can be GLUED to it at any window size/zoom (no hand-tuned
 * vw/vh). Same shape family as VideoScreenSpec/ConferenceTvSpec. Calibrate the
 * numbers live with `?screenQuad=cx,cy,cz,w,h,yaw[,tilt]` and window.__screenBox.
 */
export interface ScreenQuadSpec {
  /** World-space center of the screen plane. */
  center: [number, number, number];
  /** Plane width in metres. */
  width: number;
  /** Plane height in metres. */
  height: number;
  /** Yaw in degrees; 0 = the screen faces +Z toward a camera on +Z (default 0). */
  rotationDeg?: number;
  /** Optional tilt about X in degrees (screen leaning back), default 0. */
  tiltDeg?: number;
  /** Max camera distance (metres) at which the overlay is shown (default 8). */
  maxDistance?: number;
}

/** Screen-space axis-aligned box (viewport percent) reported by GltfScreenOverlay. */
export interface ScreenBox {
  leftPct: number;
  topPct: number;
  widthPct: number;
  heightPct: number;
  visible: boolean;
  distance: number;
}

export interface VideoScreenSpec {
  /** World-space center of the screen panel (where the video plane sits). */
  screenCenter: [number, number, number];
  /** Video-plane width in metres (spans the panel horizontally). */
  width: number;
  /** Video-plane height in metres. */
  height: number;
  /** Yaw in degrees; 0 = the screen faces +Z into the room (default 0). */
  rotationDeg?: number;
  /** Looping MP4 (CDN URL) played muted-until-unlocked, then with sound. */
  videoUrl: string;
  /** Playback volume 0..1 (default 0.35 — present but not overpowering). */
  volume?: number;
  /** Added to `volume` for the first 7 seconds, then eased back down.
   *  Default 0.32. Set 0 so this screen never rises above `volume`. */
  openBoost?: number;
  /** CRT-style brightness flicker (default true). */
  flicker?: boolean;
  /** Once playback crosses this timestamp (seconds into the clip, re-armed
   *  every loop) the volume ducks further — for a scripted "news, then hold
   *  music" clip, set this to roughly the news segment's own length so the
   *  music bed sits well under nearby dialogue instead of competing with it. */
  duckAfterS?: number;
}

export interface LaptopPropSpec {
  /** World-space point ON the surface where the laptop base sits. */
  position: [number, number, number];
  /** Yaw in degrees; 0 = the screen/keyboard face +Z (default 0). */
  rotationDeg?: number;
  /** Overall scale of the laptop (default 1). */
  scale?: number;
  /** Small heading drawn on the (idle, emissive) laptop screen. */
  screenLabel?: string;
}

export interface QrStandSpec {
  /** World-space point ON the surface where the standee base sits. */
  position: [number, number, number];
  /** Yaw in degrees; 0 = the QR face faces +Z (default 0). */
  rotationDeg?: number;
  /** Overall scale of the standee (default 1). */
  scale?: number;
  /** CDN URL of the pre-baked QR card image (QR code + caption). */
  qrImageUrl: string;
}

export interface StaticGltfPropSpec {
  /** CDN URL of the (already optimized) glTF/GLB prop. */
  url: string;
  /** World-space position of the prop's own origin. */
  position: [number, number, number];
  /** Yaw in degrees around Y (default 0 — whatever the asset's own +Z-facing
   *  orientation is out of the box). */
  rotationDeg?: number;
  /** Uniform scale (default 1 — set this only if the source asset's own unit
   *  scale isn't already metres). */
  scale?: number;
}

export interface OfficePosterSpec {
  id: string;
  /** CDN URL of the AI-generated illustration (baked into the poster face
   *  along with the headline/sub, drawn crisply via Canvas2D on top — never
   *  baked into the AI image itself). */
  imageUrl: string;
  /** Short catchy caption shown in the poster's accent band. */
  headline: string;
  /** Optional smaller sub-line under the headline (the logo poster's URL). */
  sub?: string;
  /** World-space centre of the poster face (flush-mounted on a wall). */
  position: [number, number, number];
  /** Yaw in degrees; 0 = the poster faces +Z (default 0). */
  rotationDeg?: number;
  /** Poster width in metres (default 0.46); height follows the fixed aspect
   *  of the baked texture (illustration square + caption band). */
  width?: number;
  /** If set, this is the ONE clickable poster — clicking its hotspot opens
   *  this URL in a new tab. */
  linkUrl?: string;
}

export interface DeskPhoneSpec {
  /** World-space point ON the counter surface where the phone base sits. */
  position: [number, number, number];
  /** Yaw in degrees; 0 = the handset/keypad faces +Z (toward the room). */
  rotationDeg?: number;
  /** Overall scale of the phone (default 1). */
  scale?: number;
}

export interface DeskPackageSpec {
  /** Offset from the desk's own position (metres, in the desk's local/rotated
   *  space): [right, up-from-desk-surface, forward]. Default places it beside
   *  the monitor, clear of the keyboard/mouse. */
  offset?: [number, number, number];
  /** Text on the shipping label glued to the box (default a generic label). */
  labelText?: string;
}

export interface EntryIntercomSpec {
  /** World-space point on the WALL where the panel's centre sits (flush-mounted). */
  position: [number, number, number];
  /** Yaw in degrees; 0 = the panel faces +Z (screen looks toward +Z). Set so the
   *  screen faces INTO the room (toward the approaching employee). */
  rotationDeg?: number;
  /** Overall scale of the panel (default 1). */
  scale?: number;
  /** Small label shown on the idle screen (default "ENTRY"). */
  label?: string;
}

export interface ReceptionTerminalSpec {
  /** World-space point ON THE SURFACE (e.g. counter top) where the kiosk base sits. */
  position: [number, number, number];
  /** Yaw in degrees; 0 = screen faces +Z (default 0). */
  rotationDeg?: number;
  /** Overall scale of the whole kiosk (default 1). */
  scale?: number;
  /** Short label drawn on the idle screen (default "Welcome"). */
  label?: string;
  /** Still shown on the kiosk screen before it is switched on. */
  imageUrl?: string;
}

export interface ConferenceTvSpec {
  /** World-space center of the TV panel (where the image plane sits). */
  screenCenter: [number, number, number];
  /** Image-plane width in metres (spans the panel horizontally). */
  width: number;
  /** Image-plane height in metres. */
  height: number;
  /** Yaw in degrees; 90 = the screen faces +X into the room (default 90). */
  rotationDeg?: number;
  /** The picture shown when the TV is switched on (CDN URL). */
  imageUrl: string;
}

export interface DeskPcSpec {
  /** World-space point of the desk SURFACE where the monitor foot sits. */
  position: [number, number, number];
  /** Yaw in degrees (0 = screen faces -Z toward the approaching learner). */
  rotationDeg?: number;
  /** Overall scale of the whole workstation (default 1). */
  scale?: number;
  /** Heading shown on the login screen (company / user). */
  screenTitle?: string;
  /** Username shown on the login screen. */
  userName?: string;
  /** The password scribbled on the sticky note (the teachable secret). */
  password?: string;
  /** Small caption drawn on the sticky note above the password. */
  stickyCaption?: string;
  /**
   * When true, skip modelling the physical yellow sticky note on the monitor
   * bezel entirely. Used by guided (non-walkable) courses like
   * office-under-attack where the login is taught as "click the blinking
   * login screen itself", not "find and read a sticky note" — the note prop
   * would otherwise still render even though its click-hotspot is disabled
   * for non-walkable courses (`deskPcEnabled` gates the hotspot, not the
   * mesh), leaving an inert, confusing sticky note stuck to the monitor.
   */
  hideStickyNote?: boolean;
}

export interface SplatTransform {
  /** degrees, XYZ Euler (Marble is often Y-up but rotated/flipped). */
  rotationDeg?: [number, number, number];
  /** uniform scale to bring the world into ~metres. */
  scale?: number;
  /** translation applied AFTER rotation+scale, to recentre the world. */
  position?: [number, number, number];
}

export interface AssetRef {
  /** VPS-served URL, e.g. "/cdn/sim/phishing-office/office.spz". */
  url: string;
  /** bytes — used for the loader budget + tier decisions. */
  approxBytes?: number;
  format?: "spz" | "ply" | "ksplat" | "glb" | "gltf" | "hdr" | "exr" | "jpg" | "png";
  /** Draco/KTX2 etc. */
  compression?: string;
}

/** A photoreal hero prop (Rodin/Tripo glb) placed into the scene. */
export interface PropSpec {
  id: string;
  label: string;
  asset: AssetRef;
  /** metres; applied after the model's own transforms are baked. */
  position: Vec3;
  rotationDeg?: Vec3;
  scale?: number | Vec3;
  /** If this prop is a device anchor (monitor/phone), which device it hosts. */
  hostsDevice?: DeviceId;
  /** Style-lock provenance (Art Director agent) — for re-generation/auditing. */
  styleAnchorId?: string;
}

export type Vec3 = [number, number, number];

/** Background music bed style (synthesized, no asset). */
export type MusicStyle = "office" | "cafe" | "dark" | "home" | "radio" | "train" | "none";

/** A named alternate environment a scenario can CUT to mid-course (e.g. restaurant
 *  -> hacker room -> living room). A beat carries `environmentId` to switch the
 *  active 3D scene + its ambient bed. The base `scene.environment` is the start. */
export interface SceneEnvironment {
  id: string;
  environment: EnvironmentSpec;
  /** ambient loop for this scene (overrides the scenario default while active). */
  ambientUrl?: string;
  ambientVolume?: number;
  /** music bed style for this scene. */
  music?: MusicStyle;
}

// ---------------------------------------------------------------------------
// Camera choreography
// ---------------------------------------------------------------------------

export interface CameraWaypoint {
  id: string;
  position: Vec3;
  /** look-at target in world space. */
  target: Vec3;
  fov?: number;
  /** seconds for the tween INTO this waypoint. */
  durationS?: number;
  /** easing key understood by the camera rig. */
  ease?: "linear" | "easeInOut" | "easeOut" | "easeIn";
}

// ---------------------------------------------------------------------------
// Devices (crisp DOM shells composited over the canvas)
// ---------------------------------------------------------------------------

export type DeviceId =
  | "desktop"
    | "policypage"
    | "bankstory"
    | "sendit"
    | "malwarepc"
  | "browser"
  | "phone"
  | "mail"
  | "ebanking"
  | "videocall"
  | "intercom"
  | "techrequest"
  | "usb"
  | "quishing"
  | "deepfakegen"
  | "deepfakecall"
  | "itcall"
  | "fileexplorer"
  | "ransomnote"
  | "leaksite"
  | "newscast"
  | "newsexplainer"
  | "socialpost"
  | "osintrecon"
  | "macrodoc"
  | "exfil"
  | "packlist"
  | "wifipicker"
  | "shouldersurf"
  | "publiccall"
  | "lostdevice"
  | "chargerchoice"
  | "byodgate"
  | "incidenttimeline"
  | "wifikiosk"
  | "cafecharge"
  | "byodlaptop"
  | "tailgate"
  | "privacyhunt"
  | "privacyclassify"
  | "incidentticket"
  | "incidentreport"
  | "aiassistant"
  | "barchat"
  | "barlaptop"
  | "mitmreveal"
  | "cocktailqr"
  | string;

export interface DeviceSpec {
  id: DeviceId;
  kind:
    | "desktop"
    | "policypage"
    | "bankstory"
    | "sendit"
    | "malwarepc"
    | "browser"
    | "phone"
    | "mail"
    | "ebanking"
    | "videocall"
    | "intercom"
    | "techrequest"
    | "usb"
    | "quishing"
    | "deepfakegen"
    | "deepfakecall"
    | "itcall"
    | "fileexplorer"
    | "ransomnote"
    | "leaksite"
    | "newscast"
    | "newsexplainer"
    | "socialpost"
    | "osintrecon"
    | "macrodoc"
    | "exfil"
    | "packlist"
    | "wifipicker"
    | "shouldersurf"
    | "publiccall"
    | "lostdevice"
    | "chargerchoice"
    | "byodgate"
    | "incidenttimeline"
    | "wifikiosk"
    | "cafecharge"
    | "byodlaptop"
    | "publicpc"
    | "tailgate"
    | "usbdevice"
    | "privacyhunt"
    | "privacyclassify"
    | "signin"
    | "desklogin"
    | "mfafatigue"
    | "inboxtriage"
    | "becverify"
    | "incidentticket"
    | "incidentreport"
    | "aiassistant"
    | "personallaptop"
    | "terminal"
    // Reception kiosk that opens a breach-notification course: narrated lines
    // plus tappable illustrated learning cards and a branching "finish here /
    // go see the attacks" exit (see BreachBrief.tsx).
    | "breachbrief"
    // Narrated, spec-authored teaching deck shown on the office conference TV.
    | "slidedeck"
    // Episode 13: a LIVE AI vishing call (OpenAI Realtime "fast brain" driven
    // by a staged playbook prompt) with a browser-mic-enable step + a typed
    // fallback, plus a deterministic "open the remote-support tool" compromise
    // button. `remotesupport` = the remote-desktop takeover cinematic reached
    // when the learner opens that tool.
    | "livevishing"
    | "remotesupport"
    // Bar Wi-Fi game (walkable bar, see ch.16-adjacent bar-walkable-spec.json):
    // face-to-face counter conversation, the counter laptop's full sign-in ->
    // Wi-Fi -> webmail -> SSL-warning mini-app, and the hacker-room MITM reveal.
    | "barchat"
    | "barlaptop"
    | "mitmreveal"
    | "cocktailqr"
    // "The Influencer" arc (bar-walkable, item i): the story-post/Instagram
    // slideshow, the hacker's second (social-OSINT) look glued to the same
    // hacker-room screenQuad as `mitmreveal`, the spear-phishing email draft
    // cinematic (also glued), and the rewind "edit before you post" minigame.
    | "photostory"
    | "hackerzoom"
    | "emaildraft"
    | "storyeditor"
    // pool-walkable opening: iPad in the lounger's hands (SCREEN_KINDS power gate).
    | "pooltablet"
    | "holidaytablet"
    | "holidaybriefing"
    | "holidayfakehunt"
    // glued to the hacker-room CRT via SCREEN_KINDS + screenQuad (power on, login, OSINT harvest).
    | "holidayrecon"
    | "holidaymeeting"
    | "realorai"
    | "decisiondrill"
    // The Message From IT: Teams/Slack chat (PC or iPad), iPad OAuth consent,
    // attacker cloud console.
    | "mfitchat"
    | "mfitconsent"
    | "mfitclouddash"
    // MFA fatigue course: office PC and the learner's iPhone.
    | "mfapc"
    | "mfaphone"
    // "The Fake Browser Fix" course.
    | "clickfix"
    // "One Click Too Many" course.
    | "oneclick"
    | "devday"
    | "invoice"
    | "turkeyhunt"
    | "spotfraud"
    | "invaders"
    | "arcadetv";
  /** Per-device config. `unknown` here is narrowed by each shell component. */
  config:
    | DesktopConfig
    | PolicyPageConfig
    | BankStoryConfig
    | SendItConfig
    | BrowserConfig
    | PhoneConfig
    | MailConfig
    | EBankingConfig
    | VideoCallConfig
    | IntercomConfig
    | TechRequestConfig
    | UsbConfig
    | QuishingConfig
    | DeepfakeGenConfig
    | DeepfakeCallConfig
    | ItCallConfig
    | FileExplorerConfig
    | RansomNoteConfig
    | LeakSiteConfig
    | NewscastConfig
    | NewsExplainerConfig
    | SocialPostConfig
    | OsintReconConfig
    | MacroDocConfig
    | ExfilConfig
    | PackListConfig
    | WifiPickerConfig
    | ShoulderSurfConfig
    | PublicCallConfig
    | LostDeviceConfig
    | ChargerChoiceConfig
    | ByodGateConfig
    | IncidentTimelineConfig
    | WifiKioskConfig
    | CafeChargeConfig
    | ByodLaptopConfig
    | PublicPcConfig
    | TailgateConfig
    | TerminalConfig
    | BreachBriefConfig
    | SlideDeckConfig
    | DecisionDrillConfig
    | UsbDeviceConfig
    | PrivacyHuntConfig
    | PrivacyClassifyConfig
    | SignInConfig
    | DeskLoginConfig
    | MfaFatigueConfig
    | InboxTriageConfig
    | BecVerifyConfig
    | IncidentTicketConfig
    | IncidentReportConfig
    | AiAssistantConfig
    | PersonalLaptopConfig
    | LiveVishingConfig
    | RemoteSupportConfig
    | BarChatConfig
    | BarLaptopConfig
    | MitmRevealConfig
    | CocktailQrConfig
    | PhotoStoryConfig
    | HackerZoomConfig
    | EmailDraftConfig
    | StoryEditorConfig
    | PoolTabletConfig
    | HolidayTabletConfig
    | HolidayBriefingConfig
    | HolidayFakeHuntConfig
    | HolidayReconConfig
    | HolidayMeetingConfig
    | RealOrAiConfig
    | MfitChatConfig
    | MfitConsentConfig
    | MfitCloudDashConfig
    | MfaPcConfig
    | MfaPhoneConfig
    | ClickFixConfig
    | OneClickConfig
    | DevDayConfig
    | InvoiceConfig
    | TurkeyHuntConfig
    | SpotFraudConfig
    | InvadersConfig
    | ArcadeTvConfig;
}

/** Episode 13 — a LIVE AI vishing call. Unlike the scripted `PhoneCall`
 *  (fixed lines + MFA keypad) this hands the conversation to a real OpenAI
 *  Realtime model steered by a staged playbook (see {@link VoiceAttackerSpec}
 *  `playbook`). The learner answers, enables the mic (or types instead), and
 *  talks to the caller; two explicit action buttons capture the deterministic
 *  outcomes so a compromise never depends on unreliable speech recognition. */
export interface LiveVishingConfig {
  /** which `scenario.voiceAttackers` entry drives this live call. */
  attackerId: string;
  /** caller card label, e.g. "IT Service Desk". */
  callerName: string;
  /** spoofed line caption, e.g. "Internal \u00b7 ext. 4471". */
  callerSubtitle?: string;
  /** phone-screen wallpaper (defaults to a dark gradient). */
  wallpaperUrl?: string;
  /** brand name of the remote-support tool the caller pushes, shown on the
   *  compromise button, e.g. "NorthBay Remote Assist". */
  remoteToolName?: string;
  /** completeOn key emitted when the learner OPENS the remote tool (compromise).
   *  Routed by an edge `on:` to the remote-takeover beat. Default "remote". */
  remoteKey?: string;
  /** completeOn key emitted when the learner hangs up / ends without the
   *  compromise. Routed by an edge `on:` to the resisted teach. Default "resisted". */
  endKey?: string;
}

/** Episode 13 — the remote-desktop takeover cinematic. Reached only when the
 *  learner opened the remote-support tool during the live vishing call: an
 *  autonomous cursor moves on a mock desktop, opens a confidential file, and
 *  exfiltrates it, making the "one click gave them everything" point concrete. */
export interface RemoteSupportConfig {
  /** brand shown in the tool chrome, e.g. "NorthBay Remote Assist". */
  toolName: string;
  /** the confidential file the attacker autonomously opens. */
  fileName?: string;
  /** partner id the attacker "connects" as, shown in the session banner. */
  partnerId?: string;
  continueLabel?: string;
}

/** The four real-world data-sensitivity LEVELS taught in Episode 3 ("Office
 *  privacy investigation") — Public, Internal, Confidential, and Highly
 *  Confidential/Restricted. A standard four-tier classification baseline
 *  (mirrors common enterprise schemes without naming a specific framework). */
export type PrivacyLevel = "public" | "internal" | "confidential" | "restricted";

export type PrivacyVerdict = "display" | "secure" | "screen" | "check";

export interface PrivacyLessonCard {
  title: string;
  informationType: string;
  verdict: PrivacyVerdict;
  verdictLabel: string;
  found: string;
  matters: string;
  action: string;
}

/** Secondary data-type TAG, orthogonal to {@link PrivacyLevel} — describes
 *  the KIND of data, not its sensitivity. A passport is `restricted` +
 *  `personal`; a password note is `restricted` + `credentials`. PII is
 *  deliberately NOT its own level — it's a tag that can attach to any level. */
export type PrivacyTag = "personal" | "financial" | "health" | "credentials" | "legal" | "ip";

/** One item planted as a real 3D prop in the office
 *  (`environment.privacyRoom`). Authored ONCE and reused by both Episode-3
 *  phases: Phase A (`PrivacyHunt3D.tsx`) walks the learner up to the prop in
 *  3D and reveals `reveal` on click; Phase B (`PrivacySort.tsx`) is the same
 *  item as a draggable card, scored against `level` (+ `tags` shown as a
 *  secondary check) with `explain` shown after it's dropped. */
export interface PrivacyRoomItem {
  id: string;
  /** short label, e.g. "Printed customer list". */
  label: string;
  /** single glyph/emoji shown on the in-room prop + the sort-game card. */
  icon: string;
  level: PrivacyLevel;
  tags?: PrivacyTag[];
  /** what this really is, shown once found in the room (ungraded reveal). */
  reveal: string;
  /** reflective one-liner shown under the reveal, e.g. "Who should see this?" */
  prompt: string;
  /** shown after the learner drops this card in Phase B, right or wrong. */
  explain: string;
  /** Discovery card. The sort game still uses level, reveal and explain. */
  card?: PrivacyLessonCard;
  /** world position of the CLICKABLE anchor (screen-pin origin). For
   *  `renderStyle` values that share one physical prop (e.g. two "screen"
   *  items on the same laptop) this only needs to be the point ON that
   *  prop the item represents — the physical body itself comes from
   *  {@link PrivacyRoomSpec.furniture}, not from this item. */
  position: [number, number, number];
  rotationDeg?: number;
  /** How the invisible 3D hotspot at `position` is dressed up visually.
   *  Defaults to `"flat"` (a real paper/folder lying FLAT on a surface —
   *  never a standing card). Deliberately varied per item so Episode 3
   *  doesn't read as "everything is paper": `"screen"` items sit on a
   *  `furniture` laptop's display (no body of their own), `"usb"` renders a
   *  small USB stick plugged into a laptop's port, `"printer_output"`
   *  renders a printed sheet mid-eject from a `furniture` printer, and
   *  `"passport"` renders a small closed booklet. */
  renderStyle?: "flat" | "screen" | "usb" | "printer_output" | "passport";
  /** `renderStyle:"flat"` only — true renders as a stiff closed FOLDER
   *  (manila card) instead of a loose single sheet of paper. */
  folder?: boolean;
  /** `renderStyle:"flat"` only — `"small"` renders a palm-sized yellow
   *  sticky note instead of a full sheet (e.g. a password scrawled on a
   *  Post-it stuck to a monitor bezel, not a printed page). */
  size?: "normal" | "small";
}

/** A purely decorative physical prop (no click target of its own) that one
 *  or more {@link PrivacyRoomItem}s visually sit on/in — e.g. the laptop a
 *  "screen" item's content plays on, or the printer a "printer_output" item
 *  ejects from. Rendered once by `PrivacyRoomProps.tsx`, independent of the
 *  item list, so several items can share one physical object without
 *  duplicating its geometry. */
export interface PrivacyFurnitureItem {
  kind: "desk" | "shelf" | "laptop" | "printer";
  position: [number, number, number];
  rotationDeg?: number;
  /**
   * Episode 10 reuses THIS SAME physical laptop (no second/duplicate laptop
   * prop) as "your own laptop" — setting this stamps a yellow "click to
   * open" sticky note on the bezel corner and reports its screen position
   * every frame via the furniture-level `onLaptopPin` callback (see
   * `PrivacyRoomProps`), same pattern as `deskPc`/`receptionTerminal`.
   */
  stickyCaption?: string;
}

/** Real 3D props scattered in an office environment for the Episode-3
 *  "find it, then sort it" pair. Rendered by `PrivacyRoomProps.tsx`
 *  (per-item screen-pin projection, same pattern as `DeskPackage`/
 *  `EntryIntercom`), consumed by `PrivacyHunt3D`/`PrivacySort`. */
export interface PrivacyRoomSpec {
  items: PrivacyRoomItem[];
  /** Desks/shelves/laptop/printer bodies the items above dress up or sit on. */
  furniture?: PrivacyFurnitureItem[];
}

/** Phase-A "Find the risks" HUD/reveal overlay (`PrivacyHunt3D.tsx`) — the
 *  learner walks around the ROOM in real 3D (not a static hold) and clicks
 *  real prop pins (`environment.privacyRoom.items`, projected by
 *  `PrivacyRoomProps.tsx`); this config only carries presentational copy. */
export interface PrivacyHuntConfig {
  headline?: string;
  body?: string;
  /** soft countdown in seconds (visual urgency only — never blocks Continue). */
  timerSeconds?: number;
}

/** Phase-B drag-and-drop classification game (`PrivacySort.tsx`) over the
 *  SAME items found in Phase A (`environment.privacyRoom.items`). The
 *  learner drags each card into one of the four {@link PrivacyLevel}
 *  buckets; scored with soft, explanatory feedback (never a hard fail) via
 *  `onComplete`. */
export interface PrivacyClassifyConfig {
  headline?: string;
  body?: string;
  categories: { id: PrivacyLevel; label: string; hint?: string }[];
}

/** A confident stranger asks the learner to hold a badge-gated door open (no
 *  own badge / "forgot it"). Unlike Intercom/TechRequest (forced-convergence
 *  "demonstrated mistake" devices with no real refuse path), Tailgate is a
 *  GENUINE branching decision: comply (hold the door) vs refuse (direct them
 *  to reception / ask them to badge in themselves) are both real endings,
 *  wired to `onEnded("risky"|"safe")` like VideoCall/DeepfakeCall so the
 *  scenario graph can route a risky choice into a consequence-and-rewind
 *  loop. Set `forceSafe` on a retry attempt to hide the risky option once the
 *  learner has already seen the consequence (guarantees no dead loop). */
/** One escalation round in the door social-engineering encounter. The visitor
 *  is a persistent, plausible impersonator: each time the learner declines to
 *  simply open the door, they ESCALATE (friendly courier -> name-dropping an
 *  exec -> open pressure/threats) instead of giving up. Only holding firm
 *  through the final round is the safe outcome. */
export interface TailgateRound {
  id: string;
  /** lip-synced talking-head MP4 for this round (baked voice). Self-hosted /cdn. */
  clipUrl?: string;
  /** the visitor's line for this round (captioned; verbatim of the clip audio). */
  message: string;
  /** small status line under the visitor name for this round, e.g.
   *  "Now name-dropping an executive". */
  tag?: string;
  /** label of the RISKY comply option for this round (defaults to a shared one).
   *  Picking it always ends the encounter as "risky". */
  complyLabel?: string;
  /** label of the "don't comply" option. On a non-final round this ADVANCES to
   *  the next (more aggressive) round; on the final round it ENDS as "safe". */
  refuseLabel?: string;
  /** category of the refuse/push option (default "refuse"; use "verify"/"question"
   *  for the softer early push-backs). */
  refuseCategory?: ChoiceCategory;
}

export interface TailgateConfig {
  visitorName: string;
  visitorOrg?: string;
  visitorInitials?: string;
  /** the pretext line (spoken + captioned). Used as round 1 when `rounds` is
   *  omitted (single-round back-compat). */
  message: string;
  subtext?: string;
  holdDoorLabel?: string;
  refuseLabel?: string;
  /** hide the risky option (used on the forced-correct retry beat). */
  forceSafe?: boolean;
  /** flavor line shown only when forceSafe is set. */
  retryHint?: string;
  voiceId?: string;
  language?: string;
  /** station label, e.g. "Side Entrance". Setting this switches the panel
   *  to the door-camera CCTV look (feed + scanline/flicker overlay, like
   *  Intercom) instead of the plain avatar card. */
  station?: string;
  building?: string;
  /** lip-synced talking-head MP4 of the visitor (baked voice); shown under
   *  the CCTV overlay instead of initials. Self-hosted /cdn. */
  clipUrl?: string;
  /** used on a forced-safe retry beat in place of clipUrl (same avatar +
   *  voice, a pushier line) so the face doesn't freeze on retry. */
  clipPressureUrl?: string;
  /** MULTI-ROUND escalation (preferred). When present, the learner faces each
   *  round in order: complying at ANY round ends "risky"; declining advances to
   *  the next, angrier round; declining the LAST round ends "safe". */
  rounds?: TailgateRound[];
  /** clip that plays briefly after the learner holds firm on the final round
   *  (the visitor storms off) before the "safe" outcome is recorded. */
  giveupClipUrl?: string;
  /** captioned line for the give-up clip (verbatim of its audio). */
  giveupMessage?: string;
}

/** A static informational kiosk/terminal the learner switches on themselves
 *  (e.g. a reception welcome screen standing in for a receptionist who can't
 *  be shown). Deterministic "power-on gate" (see office.glb rules): starts
 *  OFF showing a power prompt; a click reveals `lines` one at a time
 *  (spoken + captioned, same sequential pattern as Consequence.tsx) then a
 *  single Continue. No branching — this is flavor/context, not a decision. */
/** Shared union of every staged CSS/SVG dramatization kind ConsequenceVisual.tsx
 *  knows how to render — used both by `Beat.consequence.visual` (full replay)
 *  and by `Beat.terminalDebrief`'s per-topic `helper.visual` (small looping
 *  modal graphic reusing the exact same components at a smaller size). */
export type ConsequenceVisualKind =
  | "tailgate_breach"
  | "badusb"
  | "hackerterminal"
  | "ransom_takeover"
  | "ransomware_spread"
  | "leaksite"
  | "ai_shared_leak"
  | "desk_privacy"
  | "domain_spoof"
  | "deepfake_call"
  | "remote_takeover"
  | "fake_meeting";

export interface TerminalConfig {
  /** header shown once powered on, e.g. "Meridian Group — Front Desk". */
  title?: string;
  /** label on the power button before it's switched on. */
  offLabel?: string;
  /** lines revealed sequentially once powered on. */
  lines: string[];
  continueLabel?: string;
  /** When set, these replace the single Continue button. `href` leaves the course. */
  choices?: { id: string; label: string; blurb?: string; href?: string }[];
  voiceId?: string;
  language?: string;
}

/** One route launched from the breach-course kiosk hub. */
export interface BreachBriefRoute {
  id: string;
  label: string;
  blurb?: string;
  /** `edges[].on` route key emitted when selected. */
  key: string;
  /** Leave this course instead of emitting `key`. */
  href?: string;
  /** Terminal beat id whose visit marks this route complete. */
  completionBeatId: string;
  /** Small visual key rendered by BreachBrief; no external asset required. */
  icon?: "slides" | "phishing" | "download" | "wifi" | "usb" | "privacy" | "chapters";
  /** Span the full lesson row. */
  wide?: boolean;
  /** Button label before the route is complete. */
  actionLabel?: string;
  /** Button label after the route is complete. */
  doneLabel?: string;
  hint?: string;
  /** Capture or illustration shown on image-led hub cards. */
  imageUrl?: string;
}

/**
 * Reception kiosk used to OPEN a breach-notification course: the same physical
 * welcome terminal as {@link TerminalConfig} (power-on gate, narrated +
 * captioned lines) but the screen then offers illustrated learning cards and a
 * genuine branching exit instead of a single Continue. A standalone device kind
 * rather than extra optional fields on `TerminalConfig`, for the same reason
 * `terminal_debrief` is its own beat type: the shared kiosk file every other
 * course renders never has to change shape for this layout.
 */
export interface BreachBriefConfig {
  title?: string;
  offLabel?: string;
  /** lines revealed sequentially once powered on. */
  lines: string[];
  /** Compact hub summary shown after narration, under the title. */
  hubIntro?: string;
  /** Two core teaching decks; all must be completed before Finish unlocks. */
  cardsLabel?: string;
  cardsTitle?: string;
  cardsIntro?: string;
  cards: BreachBriefRoute[];
  /** Film across the top, with the exercises underneath instead of beside it. */
  videoFirst?: boolean;
  /** Optional video beside the live exercise. `src` is omitted until the file exists. */
  hubVideo?: {
    title: string;
    blurb?: string;
    src?: string;
    poster?: string;
  };
  /** Still shown on the terminal before it is switched on. */
  idleImage?: string;
  /** Optional gamified live exercises shown as image-led game cards. */
  exercisesKicker?: string;
  exercisesLabel?: string;
  exercisesIntro?: string;
  exercises: BreachBriefRoute[];
  finishLabel?: string;
  finishHint?: string;
  /** Shown under Finish while chapters are still open. */
  finishEarlyHint?: string;
  /** Hub is a menu. Do not show Finish. */
  hideFinish?: boolean;
  /** localStorage key so opened modules stay marked after a reload. */
  progressKey?: string;
  /** Status pill. Defaults to "BREACH NOTICE". */
  noticeTag?: string;
  /** Kicker beside the status pill. Defaults to "SECURITY AWARENESS BRIEFING". */
  noticeKicker?: string;
  voiceId?: string;
  language?: string;
}

export type SlideVisualKind =
  | ConsequenceVisualKind
  | "phishing_page"
  | "dark_market"
  | "credential_vault"
  | "third_party_breach"
  | "human_social"
  | "credential_stuffing"
  | "session_cookie"
  | "mailbox_trust"
  | "access_resale"
  | "login_found"
  | "protect_steps";

export interface SlideDeckSlide {
  kicker?: string;
  title: string;
  body: string;
  bullets?: string[];
  narration: string;
  visual: SlideVisualKind;
  /** Photo used instead of the coded visual when it loads. */
  imageUrl?: string;
  /** Animated chapter illustration id, drawn instead of the photo. */
  art?: string;
}

export interface SlideDecisionOption {
  id: string;
  label: string;
  correct?: boolean;
  feedback: string;
}

export interface SlideDecision {
  prompt: string;
  narration: string;
  options: SlideDecisionOption[];
}

/** Spec-authored, narrated teaching deck sized to the conference-room TV. */
export interface SlideDeckConfig {
  deckLabel?: string;
  title: string;
  slides: SlideDeckSlide[];
  voiceId?: string;
  language?: string;
  continueLabel?: string;
  /** Leaves the television and returns to the overview. Hidden when unset. */
  hubLabel?: string;
  /** Skips to the next chapter while staying on the television. Hidden when unset. */
  nextChapterLabel?: string;
  /** Realistic choice shown after the last slide. */
  decision?: SlideDecision;
  /** Label on the button that opens the decision. */
  knowHowLabel?: string;
  /** Chapter jump list. When set, the television shows a top chapter bar. */
  chapters?: { id: string; label: string }[];
  /** Index into `chapters` for this deck. */
  chapterIndex?: number;
  /** Closing reminder shown after the decision feedback. */
  takeaway?: {
    kicker?: string;
    title: string;
    body: string;
    bullets?: string[];
    narration: string;
  };
}

export interface DecisionRound {
  id: string;
  prompt: string;
  narration: string;
  options: SlideDecisionOption[];
}

/** End-of-briefing action game. */
export interface DecisionDrillConfig {
  title: string;
  intro?: string;
  rounds: DecisionRound[];
  passRatio?: number;
  continueLabel?: string;
  voiceId?: string;
  language?: string;
  passEventId?: string;
  failEventId?: string;
}

/** A single labelled choice button for {@link UsbDeviceConfig}. `key` drives
 *  which edge fires (`onEnded(key)`); `evidence`/`eventId` let the caller
 *  differentiate WHICH specific choice was made for scoring/evidence even
 *  when several choices share the same routing key (e.g. two different
 *  risky picks both routing "risky", or two safe picks both routing "safe"). */
export interface UsbChoiceOption {
  id: string;
  label: string;
  /** routing key consumed by `edges[].on` — keep the SET small (e.g. just
   *  "risky"/"safe", or "reported"/"not_reported") even with many options. */
  key: string;
  /** ScoringEvent id this choice fires (must exist in a voiceAttacker's
   *  scoringEvents[]) — omit for a choice with no scoring consequence. */
  eventId?: string;
}

/**
 * A found/delivered USB drive (or a "what do you do next" recovery moment
 * after already having inserted one) — the Episode 2 "suspicious delivery"
 * decision in `office-under-attack`. Two render modes selected by
 * `config.mode`:
 *   "decision" — shows the USB/note and the initial choice of what to do
 *     with it (insert it somewhere risky vs. hand it off / report it, never
 *     opened). The PACKAGE ITSELF is a real 3D prop on the desk
 *     (`environment.deskPackage`, rendered by `DeskPackage.tsx`) with its own
 *     "Click to open" sticky-note hotspot — this component only mounts
 *     (App.tsx gates on `packageOpened`) once the learner has already
 *     clicked it, so it always renders the OPEN contents directly.
 *   "recovery" — a second, later decision ONLY reached after a risky pick:
 *     what to do now that the fake presentation + background terminal
 *     flash already happened (report immediately vs. conceal/ignore it).
 *     This is the "reward reporting after a mistake" moment — always
 *     include a genuine `key:"reported"` option here, never a dead end.
 * `onEnded(key, choiceId)` mirrors Tailgate's `onEnded(outcomeKey)` but also
 * passes back which specific option was picked, so the caller (App.tsx) can
 * fire the right ScoringEvent/evidence text even when several options share
 * one routing key.
 */
export interface UsbDeviceConfig {
  mode: "decision" | "recovery";
  /** decision mode: what's inside the package, once opened. */
  usbLabel?: string;
  noteText?: string;
  senderLine?: string;
  /** recovery mode: what just happened, shown above the choices. */
  situationLine?: string;
  options: UsbChoiceOption[];
}

/**
 * Episode 4A ("Secure sign-in") — the workstation locked itself while the
 * learner was away doing the Episode 3 privacy walk. Signing back in is the
 * payoff of the Episode 3 sticky-note find: the SAME password shown as
 * `environment.deskPc.password` (this desk's real login) is deliberately
 * reused for WiFi/VPN too, so the note the learner just found is a working
 * credential here. A GENUINE branching decision (like Tailgate/UsbDevice),
 * never a spoiler — `showRiskLabel={false}` in `SignIn.tsx`.
 */
export interface SignInConfig {
  headline?: string;
  /** e.g. the workstation/portal name shown on the lock screen. */
  screenTitle?: string;
  userName?: string;
  /** masked password dots shown pre-filled, to sell "it's right there". */
  passwordMask?: string;
  /** the callback line tying back to the Episode 3 sticky note. */
  noteText?: string;
  options: UsbChoiceOption[];
}

/**
 * Episode 4 ("Secure sign-in") \u2014 simplified real interactive lock screen:
 * the learner actually types (or pastes) an email/password and submits, mirroring
 * the real Windows/portal login flow used across every other 3D awareness course
 * (see {@link PhishingLoginConfig} for the visual pattern this reuses). There is no
 * branching decision here \u2014 Sign In always succeeds, whatever was typed \u2014 it's
 * purely the mechanical "get past the lock screen" beat before the desktop appears.
 */
export interface DeskLoginConfig {
  /** e.g. the workstation/portal name shown on the lock screen. */
  screenTitle?: string;
  userName?: string;
  /** short hint line above the fields (e.g. pointing back to a sticky note found earlier). */
  hint?: string;
  /** the real credential the learner can copy \u2014 rendered as a small
   *  "note" card pinned to the top-right corner, next to the lock screen,
   *  so they can literally copy/paste it into the fields below. */
  credential?: { email: string; password: string; source?: string };
  continueLabel?: string;
}

/**
 * Episode 4B ("MFA fatigue") — once signed in, a burst of push-approval
 * requests hits the learner's phone/authenticator faster than they asked for
 * one, plus a Teams-style DM from an unverified "IT Support" contact urging
 * them to just approve one to "stop the glitch". `burstCount` drives an
 * animated tally of arriving push cards (cosmetic urgency only — never
 * blocks the choice). GENUINE branching decision, `onEnded(key, choiceId,
 * eventId)` mirrors {@link UsbDeviceConfig}.
 */
export interface MfaFatigueConfig {
  headline?: string;
  appName?: string;
  /** how many unsolicited push prompts have already fired before the learner acts. */
  burstCount?: number;
  chatFrom?: string;
  chatFromVerified?: boolean;
  chatMessage?: string;
  options: UsbChoiceOption[];
}

/**
 * Hidden sensitivity category of one document segment. NEVER rendered as a
 * highlight/tooltip in the preview — it only drives the scoring + the "what
 * left the company" result breakdown. Learner-facing copy avoids jargon (the
 * "credential" category is described as a "system access code (like a
 * password)", never "API key").
 */
export type AiDataCategory =
  | "personal" // a person's name / email / phone
  | "customer_identity" // the customer company's identity
  | "commercial" // confidential commercial terms (pricing, penalty)
  | "financial" // bank / payment details (IBAN)
  | "credential" // an access code / secret that works like a password
  | "internal_project" // an internal-only project codename
  | "signature" // signatures
  | "metadata"; // internal comments / review notes / metadata

/** Plain-language label per category, for the result breakdown + doc chips. */
export const AI_CATEGORY_LABEL: Record<AiDataCategory, string> = {
  personal: "Personal contact details",
  customer_identity: "Customer identity",
  commercial: "Confidential commercial terms",
  financial: "Bank / payment details",
  credential: "A system access code (like a password)",
  internal_project: "Internal project codename",
  signature: "Signatures",
  metadata: "Internal comments",
};

/** One selectable/redactable line of a document preview. Segments with no
 *  `category` are harmless or ambiguous business text — deliberately mixed in
 *  so the exercise is not a "redact everything coloured" reflex. */
export interface AiDocSegment {
  id: string;
  text: string;
  /** hidden sensitivity category; omitted = harmless/ambiguous filler. */
  category?: AiDataCategory;
  /** this line is genuinely required to answer the requested summary, so
   *  keeping it is NOT a data-minimisation failure. */
  neededForTask?: boolean;
}

export type AiClassification =
  | "Public"
  | "Internal"
  | "Confidential"
  | "Strictly confidential"
  | "Training data";

/** A document available from the employee's desktop / recent files. */
export interface AiDocumentSpec {
  id: string;
  name: string;
  fileType: "pdf" | "docx" | "xlsx";
  classification: AiClassification;
  /** one-line description shown on the file card. */
  blurb?: string;
  /** the previewable body, segment by segment. */
  segments: AiDocSegment[];
  /** original contract only: segments can be clicked to redact + the doc can
   *  be attached with those redactions applied ("create a redacted copy"). */
  redactable?: boolean;
  /** attaching this document (appropriately trimmed) can actually complete
   *  Priya's summary. Public/training docs are false — enough to be tempting,
   *  not enough to finish the task. */
  sufficientForTask?: boolean;
}

/** An AI account / workspace the learner can switch to via the profile menu. */
export interface AiWorkspaceOption {
  id: string;
  label: string;
  description: string;
  kind: "personal" | "corporate" | "shared";
}

/** How the learner chooses to get the AI's summary to the requester — the
 *  climax decision. A "public_link" creates a shareable chat URL (the big
 *  teachable risk); "copy_text" pastes only the summary; "internal_channel"
 *  writes the points into an internal reply; "guidance" pauses to ask. */
export interface AiShareOption {
  id: string;
  kind: "public_link" | "copy_text" | "internal_channel" | "guidance";
  label: string;
  sublabel?: string;
}

/** One illustrated way a public share link can leave the company, shown in the
 *  end-of-episode "how a link escapes" animation (reachable on every path). */
export interface AiLinkVector {
  /** short glyph/emoji for the card. */
  icon: string;
  title: string;
  body: string;
}

/**
 * Episode 6 ("The AI assistant dilemma") — redesigned as a realistic
 * ChatGPT-in-the-browser simulation. A Teams request asks the learner to get
 * the AI to summarise the confidential NorthBay/Horizon contract. Instead of a
 * tap-the-highlighted-words minigame, the learner runs a real chat app:
 * choose which ACCOUNT/WORKSPACE they are signed into, pick and (optionally)
 * redact WHICH document(s) to attach, edit the prompt, then send — or cancel
 * and ask for guidance. Scoring is hidden + weighted (workspace 30 / document
 * 25 / minimisation 20 / personal+confidential protection 15 / prompt+verify
 * 10) with a live-credential critical override; nothing reveals the answer
 * before the learner commits. See {@link AiChatResult}.
 */
export interface AiAssistantConfig {
  headline?: string;
  requesterName?: string;
  requesterRole?: string;
  /** the Teams message that kicks the episode off. */
  requestMessage: string;
  /** the task line shown as context inside the chat. */
  task: string;
  /** product name shown in the app + browser tab, e.g. "ChatGPT". */
  appName?: string;
  /** the learner's employer, used in the account hint ("You work for ..."). */
  companyName?: string;
  /** model name shown in the top bar, e.g. "GPT-5". */
  modelName?: string;
  /** address-bar host, e.g. "chatgpt.com". */
  chatUrl?: string;
  /** harmless previous-chat titles for the sidebar history. */
  sidebarHistory?: string[];
  /** the account/workspace choices offered in the profile menu. */
  workspaces: AiWorkspaceOption[];
  /** which workspace is selected on open (default personal, so switching to
   *  the approved one is a genuine decision rather than the pre-set state). */
  defaultWorkspaceId: string;
  /** the documents available to attach. */
  documents: AiDocumentSpec[];
  /** greyed composer placeholder. */
  promptPlaceholder?: string;
  /** a reasonable pre-typed prompt (the learner may edit it). */
  prefilledPrompt?: string;
  /** the question shown once the AI replies, framing the share decision. */
  shareQuestion?: string;
  /** how the learner can get the summary to the requester (the climax). */
  shareOptions?: AiShareOption[];
  /** neutral note shown in the "create public link" sheet — mirrors the real
   *  product ("Anyone with the link can view"), NOT a red warning. */
  publicLinkNote?: string;
  /** intro line for the end-of-episode "how a public link escapes" animation. */
  linkExposureIntro?: string;
  /** the exposure vectors illustrated in that animation. */
  linkVectors?: AiLinkVector[];
  /** closing line for the link-escape animation. */
  linkExposureClosing?: string;
}

/** The evaluated outcome handed back to App.tsx once the learner commits. All
 *  scoring is computed inside the component; App just records the eventIds and
 *  routes on `outcomeKey`. */
export interface AiChatResult {
  /** severity tier, drives the result-screen badge + copy. */
  tier: "critical" | "high" | "partial" | "best" | "safe_alt";
  /** edge-routing key: "safe" (best / asked for guidance), "risky" (any
   *  imperfect send), or "shared" (used the shared login -> consequence scene). */
  outcomeKey: "risky" | "safe" | "shared";
  /** scoring-catalogue event ids to record (map to scenario scoringEvents). */
  eventIds: string[];
  workspaceLabel: string;
  workspaceKind: "personal" | "corporate" | "shared" | "none";
  attachedDocNames: string[];
  /** categories of information that actually left the company boundary. */
  disclosed: { category: AiDataCategory; necessary: boolean }[];
  /** plain-language policy/confidentiality concerns for the result screen. */
  concerns: string[];
  /** plain-language call-outs of things the learner did RIGHT (e.g. fully
   *  redacting a sensitive file before attaching it) — shown positively on
   *  the result screen so a good redaction is never mistaken for a risk. */
  goodCalls: string[];
  /** how the learner chose to get the summary to the requester. */
  shareMethod: "public_link" | "copy_text" | "internal_channel" | "guidance" | "none";
  /** plain-language label of the share choice, for the result recap. */
  shareLabel: string;
  /** true when a public, link-anyone-can-open chat URL was created — unlocks
   *  the "how a link escapes" animation as the highlighted next step. */
  sharedPublicLink: boolean;
  /** one-line plain-language summary of what happened. */
  summaryLine: string;
  /** the recommended safer action. */
  recommendation: string;
}

/** Episode 10 — "the personal laptop": a single guided device covering the
 *  whole VPN-then-fake-game-site chain that causes the ransomware episode
 *  that follows. One component, many internal stages (see PersonalLaptop.tsx
 *  Stage union) — mirrors the ByodLaptop/PublicPc pattern of one config
 *  driving a full linear story, not a decision branch. */
export interface PersonalLaptopConfig {
  /** shown on the power-on button + spoken once the lid opens. */
  ownerNote?: string;
  /** Default `vpn`; `briefing` opens a desktop where the PDF must be read
   *  before the browser unlocks. */
  entryMode?: "vpn" | "briefing";
  /** What the installed launcher demonstrates after the game. Defaults to the
   *  original ransomware crash; breach training uses the invisible stealer. */
  outcomeMode?: "ransomware" | "infostealer";
  briefing?: PdfBriefingConfig;
  vpnAppName?: string;
  vpnProfileName?: string;
  /** resources the VPN grants access to, shown on the "connected" card. */
  vpnResources?: string[];
  /** default "popular browser games". */
  searchQuery?: string;
  /** exactly one entry needs `fake: true` — that's the one wired to advance. */
  searchResults?: { title: string; url: string; snippet: string; fake?: boolean }[];
  siteName?: string;
  gameName?: string;
  downloadFileName?: string;
}

/** A–D pack decision before leaving home for remote work.
 *  Prefer plateUrl + packable plateHotspots (click-in-room). Legacy `options`
 *  remain for fallback / older scenarios. */
export interface PackItemSpec extends HotspotSpec {
  /**
   * safe = should pack; trap = must NOT pack; info = inspect only (e.g. backpack).
   * Default when omitted: info (inspect panel only).
   */
  role?: "safe" | "trap" | "info";
  /** When role=safe, must be in the bag to pass (default true). */
  required?: boolean;
  /**
   * PREFERRED for pano pack beats: measured equirectangular texel of the real
   * object in the world pano (u,v in 0..1, u=longitude L→R, v=latitude T→B).
   * Exact — no lens/still guessing. Anchors the marker dead-on the object and
   * tracks perfectly while looking around.
   */
  u?: number;
  v?: number;
  /**
   * Sphere angles for 3D pano projection (degrees). Fallback when u/v absent.
   */
  yawDeg?: number;
  pitchDeg?: number;
  /**
   * Optional photoreal cutout (transparent PNG) composited AT the anchor so the
   * object is actually visible in the room. Use for props the generated pano
   * doesn't contain; omit for objects already present (tablet, backpack).
   * The sprite is grounded bottom-centre on the anchor and tracks the look-around.
   */
  spriteUrl?: string;
  /** Sprite width as a % of viewport width (fixed — pano has no dolly zoom). */
  spriteWidthPct?: number;
  /** Anchor point on the sprite: "bottom" (stands on surface, default) | "center". */
  spriteAnchor?: "bottom" | "center";
  /**
   * Rich "what's actually on/in this object" detail card shown when the
   * learner taps the marker — replaces the old one-line inspect panel.
   * Tapping a sticky note should show the ACTUAL handwritten password, not a
   * sentence saying "this is risky"; tapping the USB should show what's on
   * it. Omit to fall back to a plain text-only card using `detail`.
   */
  reveal?: PackRevealSpec;
}

/** Illustrated mock rendered inside a pack item's detail card. Every kind is
 *  a pure CSS/DOM mock (no photographic asset dependency / deploy step) styled
 *  to look like the real artifact — a handwritten note, a real file browser
 *  window, a lock screen, a boxed product shot. */
export interface PackRevealSpec {
  kind: "sticky_note" | "file_explorer" | "lock_screen" | "device_home" | "product";
  /** Card headline; defaults to the item's label. */
  title?: string;
  /** Small kicker above the title, e.g. "WHAT'S ON THIS DEVICE". */
  kicker?: string;
  /** Explanation shown under the illustration; defaults to item.detail. */
  narrative?: string;
  /** sticky_note: handwritten-style lines, in order. */
  noteLines?: string[];
  /** file_explorer: window chrome title, e.g. "USB Drive (E:)". */
  explorerTitle?: string;
  /** file_explorer: file rows. */
  files?: {
    name: string;
    size?: string;
    kind?: "sheet" | "doc" | "pdf" | "archive" | "text" | "image";
    sensitive?: boolean;
  }[];
  /** lock_screen: device label under the lock icon, e.g. "LAPTOP-4521". */
  deviceLabel?: string;
  /** lock_screen: small caption line, e.g. "Encrypted · Remote-wipe capable". */
  deviceCaption?: string;
  /** device_home: app icons shown on the home screen grid. */
  homeApps?: { label: string; tone?: "work" | "personal" | "alert" }[];
  /** device_home: banner/notification strip text. */
  homeBanner?: string;
  /** product: short spec chips, e.g. "Fits 14–16” laptops". */
  specs?: string[];
}

export interface PackListConfig {
  headline?: string;
  body?: string;
  /**
   * When set, pack beat uses 3D Marble pano look-around + projected pins
   * (not a 2D image pan). plateUrl is the still used to author pin %.
   */
  plateUrl?: string;
  /** Pins: xPct/yPct on the desk-facing still → sphere projection in 3D. */
  plateHotspots?: PackItemSpec[];
  /** Legacy A–D menu (unused when plateUrl is set). */
  options: {
    id: string;
    label: string;
    /** ChoiceCard category. */
    category: ChoiceCategory;
    /** scoring event id recorded on pick (risk or positive). */
    eventId?: string;
    /** edge outcome key forwarded to advance(). */
    outcome?: string;
    /** true = best / safe pack. */
    safe?: boolean;
  }[];
  lenaSafe?: string;
  lenaRisky?: string;
  voiceId?: string;
  language?: string;
}

/** Public Wi-Fi network picker with lookalike SSIDs. */
export interface WifiPickerConfig {
  headline?: string;
  body?: string;
  networks: {
    id: string;
    ssid: string;
    signal?: number;
    /** lookalike / evil twin / public trap. */
    trap?: boolean;
    /** trusted phone hotspot. */
    trusted?: boolean;
    category: ChoiceCategory;
    eventId?: string;
    outcome?: string;
  }[];
  captivePortalTitle?: string;
  captivePortalBody?: string;
  strangerLine?: string;
  lenaSafe?: string;
  lenaRisky?: string;
  voiceId?: string;
  language?: string;
}

/**
 * Public Wi-Fi self-service KIOSK (station terminal). The learner taps the
 * terminal screen (after a cinematic zoom), sees a scan of nearby networks with
 * SIGNAL + ENCRYPTION state, and connects. Exactly one network is legit: OPEN,
 * highest real signal, and it forwards to the official operator guest-pay page
 * (sbb.ch) — pay as guest, valid padlock, correct domain. Every other network is
 * a trap: an evil-twin that redirects to a look-alike SBB page (wrong domain +
 * typos, asks for a card immediately), or an encrypted one demanding company
 * credentials. After joining any trap, subsequent browsing throws an SSL/cert
 * error (the connection is being intercepted). Teaching happens on the teach beat.
 */
export interface WifiKioskConfig {
  headline?: string;
  body?: string;
  /** terminal chrome label, e.g. "Free Wi-Fi · Station terminal". */
  kioskName?: string;
  networks: WifiKioskNetwork[];
  /** look-alike operator page shown by evil-twin traps (card harvest). */
  fakePortal: {
    brand: string;
    /** the WRONG domain shown in the address bar (typosquat). */
    url: string;
    /** what the real domain should be (used in teaching). */
    legitUrl?: string;
    headline: string;
    /** visible flaws the learner should notice (wrong domain, typos, card ask). */
    flaws: string[];
    cardLabel?: string;
  };
  /** the REAL official guest-pay page (only the legit open network reaches it). */
  realPortal: {
    brand: string;
    /** correct official domain, e.g. "https://sbb.ch/wifi". */
    url: string;
    headline: string;
    body?: string;
    payLabel?: string;
  };
  /** SSL/cert interception error shown when browsing after joining a trap. */
  certError: { title: string; body: string; errorCode?: string };
  /** how to tell the real page from the fake (help toggle + carried to teach). */
  verifyTips?: string[];
  lenaSafe?: string;
  lenaRisky?: string;
  voiceId?: string;
  language?: string;
}

export interface WifiKioskNetwork {
  id: string;
  ssid: string;
  /** 0..100 signal strength. */
  signal: number;
  /** shows a padlock; open networks (legit guest wifi) are unencrypted. */
  encrypted: boolean;
  /** the ONE legitimate official guest network (open, strongest). */
  trusted?: boolean;
  trap?: boolean;
  /** "evil_twin" -> fake operator card page; "creds" -> company-login harvest. */
  trapKind?: "evil_twin" | "creds";
  category: ChoiceCategory;
  eventId?: string;
  outcome?: string;
}

/**
 * Cafe charging decision as CLICKABLE in-room objects (projected pins on the
 * Marble pano, reusing the pack pin projector). One pick decides the outcome:
 * own charger / power bank = safe; a stranger's cable or a public USB port =
 * juice-jacking trap (data goblin). Objects are baked into the pano; pins point
 * at their measured equirect texels.
 */
/** Shared hotel-business-centre public PC. Power on -> browser -> log into
 *  webmail -> read mail -> decide how to leave (sign out vs just close the tab). */
export interface PublicPcConfig {
  /** Film mode (rendered by PublicPcFilm instead of the webmail story): power
   *  on -> Browser -> sign in to an awareness portal -> watch the film, or skip
   *  it; both end the beat. */
  film?: {
    url: string;
    desktopBrand?: string;
    portalUrl?: string;
    portalBrand?: string;
    brand?: string;
    headline: string;
    body: string;
    /** labels must match the film_* entries in App.tsx PC_STAGE_GUIDE */
    playLabel?: string;
    skipLabel?: string;
    continueLabel?: string;
  };
  headline?: string;
  body?: string;
  emailUrl?: string;
  lenaSafe?: string;
  lenaRisky?: string;
  voiceId?: string;
  language?: string;
}

export interface CafeChargeConfig {
  headline?: string;
  body?: string;
  /** Body shown once the learner reaches the safe-choice stage, after the
   *  BadUSB teaching moment (learner is guided to the trap first, then shown
   *  the safe options). */
  safeBody?: string;
  /** presence marks pano-pin mode; the still used to author u/v. */
  plateUrl?: string;
  hotspots: CafeChargeHotspot[];
  goblinCaption?: string;
  lenaSafe?: string;
  lenaRisky?: string;
  voiceId?: string;
  language?: string;
}

export interface CafeChargeHotspot extends HotspotSpec {
  /** measured equirect texel of the real baked object (preferred). */
  u?: number;
  v?: number;
  /** Optional grounded cutout for an object missing from the generated pano. */
  spriteUrl?: string;
  spriteWidthPct?: number;
  spriteAnchor?: "bottom" | "center";
  safe?: boolean;
  /** trap that triggers the data-goblin exfil animation. */
  goblin?: boolean;
  eventId?: string;
  outcome?: string;
  /** line spoken/shown when this object is picked. */
  pickLine?: string;
}

/**
 * BYOD personal laptop/tablet the learner actually OPERATES. Power on -> desktop
 * shows an "OS not updated" banner -> Max is bored and the learner chooses what
 * to do: download a game from a random site (bad), copy files to personal email
 * (bad), or update + enrol in the managed work profile (good). The consequence
 * plays out on-screen, then routes to the BYOD teach.
 */
export interface ByodLaptopConfig {
  headline?: string;
  body?: string;
  /** device chrome label, e.g. "Max's personal tablet". */
  deviceLabel?: string;
  clock?: string;
  /** the update banner nagging on the desktop. */
  osBanner: string;
  /** the boredom fork the learner picks from. */
  choices: {
    id: string;
    label: string;
    /** drives the icon + which mini-flow plays. */
    kind: "game" | "update" | "email" | "screenshot";
    safe?: boolean;
    eventId?: string;
    outcome?: string;
    /** consequence card shown after picking. */
    result: { title: string; body: string; bad?: boolean };
  }[];
  lenaSafe?: string;
  lenaRisky?: string;
  voiceId?: string;
  language?: string;
}

/** Shoulder-surfing workspace decision on a train. */
export interface ShoulderSurfConfig {
  headline?: string;
  body?: string;
  /** require Inspect mode before the safe option unlocks. */
  requireInspect?: boolean;
  inspectHint?: string;
  options: {
    id: string;
    label: string;
    category: ChoiceCategory;
    eventId?: string;
    outcome?: string;
    safe?: boolean;
    /** only enabled after inspect when requireInspect. */
    needsInspect?: boolean;
  }[];
  comicFail?: string;
  lenaSafe?: string;
  voiceId?: string;
  language?: string;
}

/** Public phone-call reply picker. */
export interface PublicCallConfig {
  callerName: string;
  callerSubtitle?: string;
  opener: string;
  options: {
    id: string;
    label: string;
    category: ChoiceCategory;
    eventId?: string;
    outcome?: string;
    safe?: boolean;
    replyLine?: string;
  }[];
  lenaSafe?: string;
  voiceId?: string;
  language?: string;
}

/** Lost / forgotten laptop response. */
export interface LostDeviceConfig {
  headline?: string;
  body?: string;
  /** Optional bag hidden elsewhere in the pano; only appears after looking around. */
  bagHotspot?: PackItemSpec;
  options: {
    id: string;
    label: string;
    category: ChoiceCategory;
    eventId?: string;
    outcome?: string;
    safe?: boolean;
    hiddenInList?: boolean;
    replyLine?: string;
  }[];
  lenaSafe?: string;
  lenaRisky?: string;
  voiceId?: string;
  language?: string;
}

/** Unknown charger / USB juice-jacking decision. */
export interface ChargerChoiceConfig {
  headline?: string;
  body?: string;
  options: {
    id: string;
    label: string;
    category: ChoiceCategory;
    eventId?: string;
    outcome?: string;
    safe?: boolean;
    goblin?: boolean;
  }[];
  goblinCaption?: string;
  lenaSafe?: string;
  lenaRisky?: string;
  voiceId?: string;
  language?: string;
}

/** BYOD gate — personal tablet for work. */
export interface ByodGateConfig {
  headline?: string;
  body?: string;
  controls: { id: string; label: string; ok: boolean }[];
  options: {
    id: string;
    label: string;
    category: ChoiceCategory;
    eventId?: string;
    outcome?: string;
    safe?: boolean;
  }[];
  blockedMessage?: string;
  lenaSafe?: string;
  voiceId?: string;
  language?: string;
}

/** End-of-day clickable risk timeline. */
export interface IncidentTimelineConfig {
  kicker?: string;
  headline?: string;
  body?: string;
  howTo?: string;
  emptyPrompt?: string;
  labels?: { what?: string; why?: string; fix?: string };
  events: {
    id: string;
    title: string;
    /** Emoji/short glyph representing the scene this event came from (e.g. the
     *  Wi-Fi event uses the same icon as the kiosk beat). Falls back to the
     *  list position number when omitted. */
    icon?: string;
    what: string;
    why: string;
    fix: string;
  }[];
  continueLabel?: string;
  /** When true, Continue is always enabled and events need not be opened in
   *  order. Used for optional recaps (holiday-call attack reconstruction). */
  optional?: boolean;
}

/** Clickable room hotspot (HTML overlay projected from a 3D world anchor). */
export interface HotspotSpec {
  id: string;
  label: string;
  /** short inspect panel body. */
  detail?: string;
  /** normalised screen position 0..1 (CSS left/top %). */
  xPct: number;
  yPct: number;
}

/** Unified reply-category used by ChoiceCard across interactive dialogues. */
export type ChoiceCategory = "verify" | "question" | "refuse" | "comply";

/** An inbound IT-support vishing call on the desk phone. The learner answers,
 *  then pushes back through a few scripted "skeptical" replies — each met with a
 *  scripted EXCUSE (spoken TTS) — until the only remaining action is to comply
 *  (forced convergence: this is a "watch the mistake happen" beat; the decision
 *  is examined in the debrief + quiz, not the call). A top-right conversation
 *  panel shows the exchange. Records `trusted_unverified_it` on comply. */
export interface ItCallConfig {
  app?: string;
  /** clock in the phone's status bar, e.g. "10:40". */
  clock?: string;
  /** caller card label, e.g. "Ben — IT Service Desk". */
  callerName: string;
  /** spoofed line caption, e.g. "Internal · ext. 4471". */
  callerSubtitle?: string;
  voiceId?: string;
  language?: string;
  /** what the caller says the moment the learner answers. */
  greeting: string;
  /** ordered rounds of skeptical replies. Each pushback option carries the
   *  caller's scripted EXCUSE; advancing past the last round leaves only the
   *  `comply` option enabled. The final round SHOULD contain a comply option. */
  rounds: {
    /** optional caption above the reply buttons. */
    prompt?: string;
    options: {
      id: string;
      label: string;
      /** caller's spoken excuse when this pushback is chosen. */
      reply?: string;
      /** marks the "do as he says" option that ends the call (forced convergence). */
      comply?: boolean;
      /** safe exit: the learner hangs up to verify. Ends the call with outcome
       *  key "safe" and records `eventId` (default `verified_it_caller`). */
      hangup?: boolean;
      eventId?: string;
      /** ChoiceCard category (verify/question/refuse/comply). */
      category?: ChoiceCategory;
    }[];
  }[];
  /** caller's closing line after the learner complies (announces the email). */
  closing: string;
  /** optional lip-synced clips keyed by line id (greeting/closing/<reply ids>). */
  clips?: Record<string, string>;
}

/** A Windows-Explorer shell showing the saved attachment. The learner
 *  double-clicks the malicious file to run it; an optional UAC-style confirm
 *  appears. Records `executed_attachment`. completeOn `run_file:<id>`. */
export interface FileExplorerConfig {
  windowTitle?: string;
  /** breadcrumb path, e.g. "This PC > Downloads". */
  path?: string;
  files: {
    id: string;
    name: string;
    /** drives the icon + colour. */
    type?: "exe" | "doc" | "pdf" | "img" | "zip" | "folder";
    size?: string;
    date?: string;
    /** the file the learner is guided to run. */
    danger?: boolean;
  }[];
  /** the file id that ends the beat when run. */
  runFileId: string;
  /** optional Windows "User Account Control" style confirm before it runs. */
  runConfirm?: { title: string; body: string; runLabel: string; cancelLabel?: string };
}

/** The ransomware takeover screen: the monitor goes red, a lock glyph + a live
 *  countdown + a payment demand + an "encrypted files" list. Cinematic (no
 *  branching); completeOn `continue`. */
export interface RansomNoteConfig {
  title?: string;
  /** render the note INSIDE the monitor (sized to --scr-w/--scr-h, with a
   *  taskbar) instead of full screen. The camera must be on the screen pose. */
  onScreen?: boolean;
  /** taskbar entry of the running malware when `onScreen`. */
  processName?: string;
  /** taskbar clock when `onScreen`. */
  clock?: string;
  /** victim org shown in the note. */
  org?: string;
  body: string;
  /** demand, e.g. "0.85 BTC (~ €58,000)". */
  amount: string;
  walletLabel?: string;
  wallet?: string;
  /** countdown shown ticking, in seconds (e.g. 48h compressed for the demo). */
  deadlineSeconds?: number;
  /** "encrypted" filenames that scroll for effect. */
  fileList?: string[];
  continueLabel?: string;
}

/** The attacker-side pay-off: a dark-web leak blog OR marketplace listing. A
 *  "new victim" notification arrives, then the operator publishes. Cinematic,
 *  modeled on the deepfake studio. completeOn `continue`. */
export interface LeakSiteConfig {
  /** "blog" (default) = extortion leak site; "market" = darknet marketplace. */
  mode?: "blog" | "market";
  siteName?: string;
  victim: string;
  newVictimToast?: string;
  /** the published extortion / listing post. */
  post: { headline: string; body: string; deadline?: string; sample?: string[] };
  /** marketplace listing extras (mode=market). */
  market?: {
    price: string;
    priceSub?: string;
    packageItems?: string[];
    buyersOnline?: number;
    saleToast?: string;
  };
  /** hacker mutter (TTS). */
  line?: string;
  voiceId?: string;
  language?: string;
  /** publish pipeline steps shown completing. */
  steps?: string[];
  continueLabel?: string;
}

/** Victim oversharing on Google Reviews / Instagram. */
export interface SocialPostConfig {
  platform: "google_review" | "instagram" | string;
  authorName: string;
  authorHandle?: string;
  avatarUrl?: string;
  business?: string;
  location?: string;
  rating?: number;
  caption: string;
  photos?: string[];
  postLabel?: string;
  postedToast?: string;
  continueLabel?: string;
  line?: string;
  revealRatingMs?: number;
  voiceId?: string;
  language?: string;
}

/** Attacker OSINT recon console. */
export interface OsintReconConfig {
  appName?: string;
  company: string;
  line?: string;
  scanLine?: string;
  voiceId?: string;
  language?: string;
  employees: {
    name: string;
    role?: string;
    email: string;
    photoUrl?: string;
    target?: boolean;
  }[];
  target: {
    name: string;
    email: string;
    photoUrl?: string;
    findings?: {
      source: string;
      label: string;
      detail: string;
      photoUrl?: string;
    }[];
  };
  continueLabel?: string;
}

/** Word-style macro document (Enable Content detonates). */
export interface MacroDocConfig {
  appName?: string;
  fileName?: string;
  bannerText?: string;
  enableLabel?: string;
  heading?: string;
  body: string;
  fields: { id: string; label: string; placeholder?: string; value?: string }[];
  autofillLabel?: string;
  submitLabel?: string;
  footholdNote?: string;
  continueLabel?: string;
}

/** Attacker exfiltration console (copy shares + plant lures). */
export interface ExfilConfig {
  appName?: string;
  path?: string;
  victimOrg?: string;
  line?: string;
  voiceId?: string;
  language?: string;
  shares?: { name: string; size?: string }[];
  plantedFiles?: { name: string; type?: "exe" | "doc" | "pdf" | "zip" | "xls" }[];
  spreadToast?: string;
  continueLabel?: string;
}

/** A wall-mounted office TV running a breaking-news report. The anchor is a
 *  DEEPFAKE talking-head clip (avatar provider) — the "fancy" payoff showing the
 *  attack on the news + the bank's stock collapsing. Falls back to a portrait +
 *  TTS read of `script` when no clip is set. completeOn `continue`. */
export interface NewscastConfig {
  channel?: string;
  /** label of a skip button shown for the whole broadcast; it advances with
   *  outcome key "skip" (falls back to the default edge). */
  skipLabel?: string;
  anchorName?: string;
  /** the breaking-news lower-third banner. */
  lowerThird: string;
  /** scrolling ticker text. */
  ticker?: string;
  /** lip-synced anchor MP4 (the deepfake). When set it carries the audio. */
  clipUrl?: string;
  /** anchor portrait used as the clip poster + the TTS-fallback face. */
  posterUrl?: string;
  /** spoken script (must equal the clip audio; used as TTS when no clip). */
  script: string;
  voiceId?: string;
  language?: string;
  /** the falling stock chart overlaid on the bulletin. */
  stock?: { symbol: string; change: string; points?: number[] };
  continueLabel?: string;
}

/** A news "data segment" that continues the bulletin: the anchor (PiP portrait)
 *  walks through a few full-screen data slides about the wider ransomware threat
 *  — exploding global cost, who gets hit, and how it almost always starts with a
 *  person — each with an animated chart and a spoken (TTS) narration line. The
 *  learner advances slide by slide, then continues to the quiz. Cinematic /
 *  asset-only (no scoring). completeOn `continue`. */
export interface NewsExplainerConfig {
  channel?: string;
  /** label of a skip button next to Next; advances with outcome key "skip". */
  skipLabel?: string;
  anchorName?: string;
  /** anchor portrait shown in the picture-in-picture corner. */
  posterUrl?: string;
  voiceId?: string;
  language?: string;
  /** fixed lower-third label for the whole segment, e.g. "RANSOMWARE EXPLAINED". */
  lowerThird?: string;
  /** optional bridge clip/text spoken straight-to-camera before the data slides. */
  bridgeClipUrl?: string;
  bridgeText?: string;
  segments: NewsExplainerSegment[];
  continueLabel?: string;
}

export interface NewsExplainerSegment {
  /** small kicker above the title, e.g. "THE COST". */
  kicker?: string;
  title: string;
  /** spoken anchor narration for this slide (TTS). */
  narration: string;
  chart: NewsExplainerChart;
}

export type NewsExplainerChart =
  /** vertical bars, typically a time series (cost over the years). */
  | {
      type: "bars";
      caption?: string;
      source?: string;
      bars: { label: string; value: number; display: string; hot?: boolean }[];
    }
  /** horizontal bars (share by sector / entry point). */
  | {
      type: "hbars";
      caption?: string;
      source?: string;
      bars: { label: string; value: number; display: string; hot?: boolean }[];
    }
  /** a single big number with supporting bullets. */
  | {
      type: "stat";
      big: string;
      sub: string;
      caption?: string;
      source?: string;
      bullets?: string[];
    };

/** QR-phishing ("quishing") on a phone: the learner scans a menu QR sticker,
 *  the phone offers to open a TYPOSQUAT site, then the page over-asks for camera
 *  / microphone / location, and harvests data while a (TTS) family conversation
 *  plays over the menu. One device, driven through stages by the beats. */
export interface QuishingConfig {
  lockClock?: string;
  /** sticker caption, e.g. "Scan for Menu". */
  qrLabel?: string;
  /** the "open this site?" prompt — `host` is the typosquat shown big. */
  openPrompt: { host: string; full: string; legit?: string };
  /** the over-reaching permission prompts the page fires, in order. */
  permissions: { id: "camera" | "microphone" | "location"; title: string; body: string; allowLabel?: string }[];
  /** the fake restaurant menu the learner scrolls. */
  menu: {
    brand: string;
    url: string;
    sections: { name: string; items: { name: string; price: string; desc?: string }[] }[];
  };
  /** the victim + family chat that plays (TTS) while browsing — shows the mic is
   *  capturing real conversation. `speaker` picks the voice/side. */
  conversation: { speaker: "giovanni" | "maria"; name: string; text: string; voiceId?: string }[];
  /** what the attacker side shows being captured (camera/mic/location/contacts). */
  captureItems?: string[];
  /** the captured selfie shown in the attacker panel (the victim's portrait). */
  capturePhotoUrl?: string;
  language?: string;
}

/** The hacker's "deepfake studio" cinematic: the captured photo + voiceprint are
 *  uploaded into a deepfake app and a face/voice model trains with a progress
 *  bar, ending in a ready preview. Pure DOM cinematic over a dark 3D room. */
export interface DeepfakeGenConfig {
  appName?: string;
  /** the source photo the hacker drags in (the victim's captured portrait). */
  sourcePhotoUrl?: string;
  sourceName?: string;
  /** ordered pipeline steps shown as they "complete". */
  steps?: string[];
  /** optional hacker mutter (TTS). */
  line?: string;
  voiceId?: string;
  continueLabel?: string;
  language?: string;
}

/** The pay-off: a deepfake VIDEO call from "your son". Mirrors VideoCallConfig
 *  but framed as a personal mobile emergency call (family-emergency / "hi mum"
 *  scam) with an account/IBAN to wire to. Branching decision is the lesson. */
export interface DeepfakeCallConfig {
  app?: string;
  callerName: string;
  callerSubtitle?: string;
  relationship?: string;
  avatarUrl?: string;
  avatarInitials?: string;
  clips?: Partial<Record<"greeting" | "request" | "pressure" | "deflect" | "goodbyeFail" | "goodbyeVerify", string>>;
  voiceId?: string;
  language?: string;
  /** the wire the deepfake pushes (shown as an on-screen "send money" sheet). */
  payment: { amount: string; recipient: string; iban?: string; reference?: string; deadline?: string };
  lines: {
    greeting: string;
    request: string;
    pressure: string;
    deflect: string;
    goodbyeFail: string;
    goodbyeVerify: string;
  };
  /** deepfake tells highlighted on "look closely". */
  artifacts?: string[];
  decision?: { send: string; ask: string; verify: string };
}

/** A door-entry video intercom / speaker phone (physical social engineering).
 *  A visitor buzzes; their feed + spoken pretext appear; the learner decides to
 *  buzz them in (the demonstrated mistake) or verify first. */
export interface IntercomConfig {
  /** station label, e.g. "Main Entrance". */
  station?: string;
  /** building / company shown on the panel. */
  building?: string;
  visitorName: string;
  /** claimed affiliation, e.g. "Headquarters IT Support". */
  visitorOrg?: string;
  visitorInitials?: string;
  /** lip-synced talking-head MP4 of the visitor (baked voice). When set, the
   *  intercom shows the live face (under a CCTV flicker) instead of initials and
   *  does NOT also TTS the line (the clip carries the audio). Self-hosted /cdn. */
  clipUrl?: string;
  /** second lip-synced clip (SAME avatar + voice) for the pressure line, played
   *  when the learner tries to stall — so the face keeps moving and the voice
   *  stays the same instead of freezing + switching to TTS. */
  clipPressureUrl?: string;
  /** the pretext shown as a caption (and spoken over the speaker). */
  message: string;
  /** secondary line under the message. */
  subtext?: string;
  /** what the visitor says if the learner tries to verify (social pressure). */
  pressure?: string;
  openLabel?: string;
  verifyLabel?: string;
  /** Second refusal shown after the pressure line. Picking it completes the
   *  beat as the safe choice (`onVerify`). Without `onVerify` the device keeps
   *  its old one-way behaviour. */
  holdLabel?: string;
  /** TTS line spoken when the panel opens. */
  speak?: string;
  voiceId?: string;
  language?: string;
}

/** An off-screen technician (already inside) asking to operate the PC. The
 *  speaker is heard + captioned from the side; no avatar is required. */
export interface TechRequestConfig {
  speakerName: string;
  speakerOrg?: string;
  /** the headline ask shown above the captions. */
  request: string;
  /** scripted lines spoken (TTS) + shown as side captions, in order. */
  lines: string[];
  /** reply when the learner tries to push back (social pressure). */
  pressure?: string;
  allowLabel?: string;
  denyLabel?: string;
  /** Second refusal shown after the pressure line; completes the beat as the
   *  safe choice (`onDeny`). */
  holdLabel?: string;
  voiceId?: string;
  language?: string;
}

/**
 * Bar Wi-Fi game (walkable bar): a sequential, non-branching face-to-face
 * conversation at the counter — the bartender gives the real guest network
 * name, then a stranger on the next stool talks up a fake "faster" one. Each
 * line gets its own portrait + TTS voice (mirrors `TechRequest`/`Intercom`'s
 * sequential-caption pattern but with MULTIPLE named speakers instead of
 * one). The learner's own lines are shown as plain quote bubbles with no
 * portrait/TTS (there is no face to render for "you").
 */
export interface BarChatConfig {
  lines: {
    speaker: "learner" | "bartender" | "stranger";
    name: string;
    /** Short descriptor shown under the name so it's unambiguous WHO is
     *  physically speaking in the 3D scene (e.g. "the woman on the stool
     *  beside you") — the stranger has no dedicated 3D character model of
     *  her own (she's part of the purchased bar's baked-in patrons), so the
     *  card is what makes "it's her" legible, not a scene highlight. */
    speakerHint?: string;
    /** CDN portrait image; omitted for `speaker: "learner"`. */
    portraitUrl?: string;
    line: string;
    voiceId?: string;
    /** TTS delivery steering (OpenAI `instructions`) — e.g. giving the
     *  learner's own line a distinct spoken voice/cadence from the NPCs'. */
    voiceInstructions?: string;
    /** Fires a camera-rig waypoint change the instant this line becomes the
     *  active speaker (before its TTS starts), e.g. rotating to face the
     *  baked-in patron as she starts talking so it's visually clear who is
     *  speaking, not just the speakerHint text. */
    cameraWaypointId?: string;
  }[];
  continueLabel?: string;
  /** Short "recommended path forward" note pinned top-right of the card
   *  (e.g. "Best move: ask staff to confirm the network by name"). Purely
   *  educational — the story still forces the trap network afterward. */
  tip?: string;
}

/**
 * Bar Wi-Fi game (walkable bar): the counter laptop's full mini-app —
 * lock screen -> Windows-11-style desktop/taskbar with a REAL clickable Wi-Fi
 * flyout (mirrors the actual OS affordance the learner uses every day,
 * unlike the public-terminal-style `WifiKioskConfig`) -> webmail sign-in ->
 * a fake SSL/certificate warning the learner clicks through. One config
 * drives the whole linear stage sequence (see `BarLaptop.tsx`).
 */
export interface BarLaptopConfig {
  /** lock-screen display name (default "A. Morgan"). */
  userName?: string;
  /** lock-screen portal/workstation label. */
  screenTitle?: string;
  /** taskbar clock text, e.g. "9:41 PM" (cosmetic only). */
  clock?: string;
  networks: BarLaptopNetwork[];
  /** Google-style search results shown once the learner opens the Mail tile
   *  or the desktop Browser icon — one CORRECT domain plus one-or-more
   *  lookalike/typosquat decoys the learner must tell apart before the site
   *  opens (immediately behind an SSL warning — see `sslWarning` below).
   *  Picking a decoy shows `lookalikeExplainer` inline, then lets them try
   *  again rather than dead-ending the story. */
  googleSearch: {
    query: string;
    results: GoogleResultSpec[];
    lookalikeExplainer: string;
  };
  /** Hushed "inner thought" line (TTS with a contemplative delivery) played
   *  once the Wi-Fi flyout is first opened — the learner is visibly unsure
   *  which network to pick, so this dramatizes that hesitation instead of
   *  leaving the choice unexplained. */
  innerVoiceLine?: string;
  /** Short "recommended path forward" note pinned top-right of the Wi-Fi
   *  flyout, e.g. "Best move: ask staff, or use your own hotspot." Purely
   *  educational — the story still forces the trap network afterward. */
  wifiTip?: string;
  /** pinned Mail tile / desktop Browser icon; both open `googleSearch`. */
  webmail: {
    brand: string;
    url: string;
    userEmail: string;
    headline?: string;
    /** Plaintext password surfaced as a copy-paste "sticky note" hint once
     *  the SSL warning has been clicked through (same transparency
     *  convention as `deskPc.password` on the walkable-office course) — the
     *  point of this beat is the Wi-Fi/SSL risk, not a guessing game. */
    password: string;
  };
  sslWarning: {
    title: string;
    body: string;
    errorCode?: string;
    continueLabel?: string;
    /** Short off-voice reaction (TTS, muttered under the breath) spoken the
     *  instant this warning first appears, e.g. "Huh... SSL error. Strange."
     *  — dramatizes the moment of hesitation instead of a silent screen. */
    voiceOverLine?: string;
    /** Short "what does this mean?" label for the info button shown on the
     *  warning card; clicking it opens an explainer slide about SSL/MITM
     *  interception (see `SslInfoModal.tsx`). Defaults to a generic label. */
    infoLabel?: string;
  };
}

/** One Google-style search result card. Exactly one entry should have
 *  `correct: true`, and its `url` must equal `BarLaptopConfig.webmail.url`. */
export interface GoogleResultSpec {
  id: string;
  /** the green breadcrumb URL under the title, e.g. "mail.acmecorp-portal.com". */
  url: string;
  title: string;
  snippet: string;
  correct: boolean;
}

export interface BarLaptopNetwork {
  id: string;
  ssid: string;
  /** 0..100 signal strength (SignalBars). */
  signal: number;
  /** shows a padlock + prompts for a network security key when clicked. */
  secured: boolean;
  /** "connects" = the ONE network that actually gets online (open,
   *  strongest bars — no lock, exactly like a real open Wi-Fi); "wrong_password"
   *  = a secured network the learner doesn't have the key for (real Windows
   *  "Network security key is incorrect" inline error, collapses back to the
   *  list); "no_internet" = connects then immediately drops with a real
   *  Windows "No Internet" status. */
  outcome: "connects" | "wrong_password" | "no_internet";
}

/**
 * Bar Wi-Fi game (walkable bar): the hacker-room reveal, an animated
 * man-in-the-middle diagram on the attacker's own screen (mirrors
 * `ChargeExfil`'s "show the threat" cinematic — tension audio bed, live
 * progress, a `breachAlert` sting on completion — but drawn as data travelling
 * along a 3-node path instead of a scrolling file list).
 */
export interface MitmRevealConfig {
  /** left node label (default "Your laptop"). */
  victimLabel?: string;
  /** middle node label — the attacker's rogue hotspot, e.g. "FREE-BAR-WIFI-SUPERFAST (his hotspot)". */
  hotspotLabel?: string;
  /** right node label (default "Internet / company systems"). */
  targetLabel?: string;
  /** labelled packets animated one at a time from victim -> hotspot -> target,
   *  pulsing at the hotspot node each time (e.g. ["Login", "Work email", "Session cookie"]). */
  packets: string[];
  /** narration lines (TTS) — written as the ATTACKER'S OWN self-talk (he's
   *  narrating his evening to himself, not lecturing the learner in 2nd
   *  person), sequenced with the animation. */
  narration: string[];
  voiceId?: string;
  language?: string;
  continueLabel?: string;
  /** Room caption shown once on entry (e.g. "His apartment \u2014 across the
   *  street from the bar"), so it's unmistakable this is a different
   *  location/person, not a continuation of the bar scene. */
  roomLabel?: string;
  /** Mirrored recreation of the exact steps the learner just took on the
   *  laptop, replayed on the attacker's second monitor (e.g. "Searched
   *  Google", "Clicked the look-alike result", "Opened the mail app",
   *  "Typed in a password") — one highlighted at a time, roughly in step
   *  with the narration/packet animation. */
  replaySteps?: string[];
  /** Terminal lines that type out progressively on the attacker's screen — a
   *  live "credential extraction" feed, e.g. ["$ intercepting traffic\u2026",
   *  "[+] email: name@company.com", "[+] password: captured", ...]. */
  terminalLines?: string[];
  /** Narration-line index (0-based) at which each diagram node first appears
   *  — lets the "who's on the wire" diagram build up ONE element at a time in
   *  step with the monologue instead of dumping all three nodes on screen at
   *  once. Default 0 for every node (all visible immediately) preserves the
   *  old behaviour for any other caller. */
  hotspotRevealAt?: number;
  victimRevealAt?: number;
  targetRevealAt?: number;
}

/**
 * Bar Wi-Fi game, scene 2 — "The Free Cocktail QR Code": the phone chrome for
 * a QR-phishing + credential-theft mini-story (mirrors `QuishingPhone`'s
 * lock-screen/scan/sheet conventions), driven across three device beats:
 *   scan     -> viewfinder over the "SCAN HERE FOR A FREE COCKTAIL" sticker,
 *               a bartender aside plays underneath, then the "open site?" sheet.
 *   mslogin  -> a fake "Sign in with your company Microsoft account" page +
 *               Tom's hushed inner-monologue TTS.
 *   decision -> a 5-option decision list (one risky, four safe). Choosing
 *               "inspect the sticker" branches into an inline peel interaction
 *               before continuing. Whatever is chosen, a "What would have
 *               happened?" button opens a self-contained step-by-step preview
 *               of BOTH the risky and the safe outcome (no real re-branch —
 *               see `CocktailQrPhone.tsx`), then Continue reports the final
 *               outcome key so the beat graph's edges route accordingly.
 */
export interface CocktailQrConfig {
  lockClock?: string;
  /** decorative sticker caption, e.g. "Scan for a free cocktail". */
  qrLabel?: string;
  /** the "open this site?" prompt — `host` is the phishing domain shown big. */
  openPrompt: { host: string; full: string; legit?: string };
  /** Tom's off-voice line as the phone comes up (thirsty, spotted the sign). */
  thirstyLine?: string;
  /** heard from off-screen WHILE the QR sheet is up — the bartender didn't
   *  place this code. */
  bartenderLine?: string;
  /** Tom's spoken reply dismissing the bartender's warning. */
  ignoreLine?: string;
  bartenderVoiceId?: string;
  /** the fake "sign in with your company Microsoft account" page. */
  msLogin: {
    brand?: string;
    /** shown as the signed-out tenant context, e.g. "Acme Corp". */
    org?: string;
    userEmail: string;
    /** hushed TTS inner-monologue read once the page is up. */
    innerVoiceLine: string;
    /** the on-screen nudge to the learner, e.g. "Is this really the best choice?" */
    nudge?: string;
    continueLabel?: string;
    /** Inline Yes/No decision rendered at the bottom of the fake login page
     *  (replaces the old single Continue -> separate decision beat). "Yes"
     *  advances with outcome `qr_yes` (straight to the breach news cut); "No"
     *  reveals a short "good choice" panel offering to see the what-if
     *  (`qr_no_curious`) or skip ahead (`qr_no_skip`). All copy optional. */
    yesLabel?: string;
    noLabel?: string;
    goodChoiceTitle?: string;
    goodChoiceBody?: string;
    seeWhatIfLabel?: string;
    skipLabel?: string;
  };
  decision: {
    prompt?: string;
    options: CocktailQrOption[];
  };
  /** the "inspect the sticker" branch's peel interaction. */
  peel: {
    /** top layer — the fake bait sticker (matches the 3D qrStand prop art). */
    stickerImageUrl: string;
    /** revealed underneath — the bar's real, official QR code. */
    legitImageUrl: string;
    instructionLabel?: string;
    revealedLabel?: string;
    revealLine?: string;
    continueLabel?: string;
  };
  /** the "what would have happened" step-by-step preview, shown from the
   *  final recap screen regardless of which option was picked. */
  reveal: {
    recapTemplate?: string; // "{choice}" is replaced with the chosen option's resultLine
    whatIfLabel?: string;
    badTitle?: string;
    badSteps: string[];
    safeTitle?: string;
    safeSteps: string[];
    continueLabel?: string;
  };
  voiceId?: string;
  language?: string;
}

/** One of the 5 decision-list choices on {@link CocktailQrConfig}. Exactly one
 *  should be the risky choice (`safe: false`); the other four are safe. */
export interface CocktailQrOption {
  id: string;
  label: string;
  safe: boolean;
  /** outcome key passed to `sim.advance()`, matched by the decision beat's
   *  outgoing edges (e.g. "qr_bad_creds" | "qr_safe_peel" | "qr_safe_bartender"
   *  | "qr_safe_official" | "qr_safe_report"). */
  outcome: string;
  /** when true, choosing this option first opens the sticker-peel interaction
   *  (see `CocktailQrConfig.peel`) before the recap/reveal screen. */
  requiresPeel?: boolean;
  /** short past-tense recap line for the final screen, e.g. "You peeled back
   *  the sticker and found the real menu QR underneath." */
  resultLine: string;
}

/** One photo used across the influencer story arc (posted, browsed, and later
 *  offered again in {@link StoryEditorConfig}). */
export interface PhotoStoryPhoto {
  url: string;
  alt?: string;
}

/**
 * "The Influencer" arc (bar-walkable, item i) — a full-viewport cinematic
 * that either (a) shows the stranger COMPOSING and posting a social story
 * (typed caption reveal + camera-shutter SFX + photos landing one at a time),
 * or (b) shows Tom BROWSING that same post the next day on his phone
 * (a lightweight Instagram-style feed chrome, tap-to-open like other phone
 * apps elsewhere in this codebase). Same photo set, two presentations, no
 * new environment needed.
 */
export interface PhotoStoryConfig {
  mode: "post" | "feed";
  /** "post" mode: her caption, revealed line by line before the shutter SFX
   *  and photos land (e.g. ["Let's make a quick story...", "#CyberSecurity #AcmeBank #1664Bar #Havefun"]). */
  captionLines?: string[];
  /** "feed" mode: the app icon label shown before the tap-to-open gate, e.g. "Instagram". */
  appLabel?: string;
  /** Off-voice narration line spoken once before the cinematic starts (e.g.
   *  "Next day, Tom's back at his desk..."). Optional either mode. */
  narratorLine?: string;
  /** "feed" mode only: a second off-voice line spoken the moment the learner
   *  taps the app icon open (i.e. while he's actually looking at the post) —
   *  a private second thought/regret, distinct from the establishing
   *  `narratorLine` spoken on mount. Optional. */
  reflectionLine?: string;
  photos: PhotoStoryPhoto[];
  hashtags?: string[];
  voiceId?: string;
  language?: string;
  continueLabel?: string;
}

/**
 * "The Influencer" arc — the hacker's SECOND look, glued to the same
 * hacker-room `screenQuad` as {@link MitmRevealConfig} (via the `screenBox`
 * prop, same projector mechanism). He scrolls the same public post, then
 * zooms into one photo's background where a laptop screen is visible,
 * revealing a confidential document — the OSINT pivot into a targeted
 * spear-phish, without any new 3D asset (just a second, cropped composite
 * image of the same photo).
 */
export interface HackerZoomConfig {
  photos: PhotoStoryPhoto[];
  /** index into `photos` that gets the zoom-crop treatment. */
  targetPhotoIndex: number;
  /** the cropped/zoomed composite revealing the confidential doc/laptop in
   *  the background of that photo. */
  zoomImageUrl: string;
  /** attacker self-talk narration (mirrors `MitmRevealConfig.narration`'s
   *  convention — he's talking through what he's finding, not lecturing the
   *  learner in 2nd person). */
  narration: string[];
  voiceId?: string;
  language?: string;
  continueLabel?: string;
}

/**
 * "The Influencer" arc — the spear-phishing email draft, glued to the same
 * hacker-room `screenQuad`. A typed-out reveal of a short, highly-targeted
 * message referencing the confidential detail just spotted in the photo.
 */
export interface EmailDraftConfig {
  toName: string;
  toEmail?: string;
  fromLabel?: string;
  subject: string;
  /** typed out sequentially, one line landing at a time. */
  bodyLines: string[];
  /** attacker self-talk spoken while typing (optional). */
  narrationLine?: string;
  sendingLabel?: string;
  sentLabel?: string;
  voiceId?: string;
  language?: string;
  continueLabel?: string;
}

/** One candidate item (photo or hashtag) in the {@link StoryEditorConfig}
 *  rewind minigame — individually toggleable "keep in the post" / "cut it". */
export interface StoryEditorItem {
  id: string;
  /** photo items render `url`/`alt`; hashtag items render `label` as text. */
  url?: string;
  alt?: string;
  label?: string;
  /** true if KEEPING this item in the final post is the risky choice. */
  risky: boolean;
  /** shown in the result breakdown when a risky item was kept. */
  riskNote: string;
  /** shown in the result breakdown when a risky item was correctly cut. */
  safeNote?: string;
}

/**
 * "The Influencer" arc, finale — the "rewind" minigame: the learner is handed
 * the exact same batch of candidate photos + hashtags from the posting scene
 * and individually toggles each one "post" / "don't post" before Tom's
 * co-worker actually publishes the story. On submit, a risk score/breakdown
 * explains which kept items were still risky and why (mirrors the existing
 * `cqr-recap`/`reveal` "what would have happened" tone).
 */
export interface StoryEditorConfig {
  intro?: string;
  photos: StoryEditorItem[];
  hashtags: StoryEditorItem[];
  submitLabel?: string;
  continueLabel?: string;
  resultTitle?: string;
}

/** pool-walkable opening iPad. The App-level `.screen-power` gate turns the
 *  3D tablet on; this config is the lock/home chrome that then fills `--scr-*`. */
export interface PoolTabletConfig {
  lockTime?: string;
  lockDate?: string;
  ownerName?: string;
  continueLabel?: string;
}

export interface HolidayTabletConfig {
  mode: "wifi" | "inbox_linkedin" | "linkedin_profile" | "linkedin_reveal" | "home";
  ownerName?: string;
  time?: string;
  continueLabel?: string;
  networks?: { id: string; name: string; locked: boolean; signal?: number }[];
  profile?: {
    name: string;
    title: string;
    company: string;
    location: string;
    connections: string;
    mutual: string;
    avatarUrl?: string;
    message: string;
    about: string;
    experience: { role: string; company: string; years: string }[];
    posts: { text: string; meta: string; imageUrl?: string }[];
    recommendations: string[];
    joined?: string;
    companyUrl?: string;
    postBurstNote?: string;
  };
  /** Annotated facts shown when the fake profile is revisited near the end. */
  revealNotes?: { label: string; value: string; tone?: "risk" | "neutral" }[];
}

export interface HolidayBriefingConfig {
  sender: string;
  subject: string;
  intro: string;
  videoTitle: string;
  presenterName: string;
  presenterRole: string;
  videoUrl?: string;
  posterUrl?: string;
  arupSourceUrl: string;
  googleSourceUrl: string;
}

export interface HolidayFakeHuntProfile {
  id: string;
  name: string;
  headline: string;
  company: string;
  location: string;
  connections: string;
  joined: string;
  initials: string;
  photoUrl?: string;
  about: string;
  activity: string;
  contact?: string;
  hint?: string;
  tell: {
    zone: "photo" | "name" | "joined" | "activity" | "company" | "contact";
    label: string;
    why: string;
  };
}

export interface HolidayFakeHuntConfig {
  kicker?: string;
  title: string;
  intro: string;
  closer: string;
  profiles: HolidayFakeHuntProfile[];
}

export interface HolidayReconConfig {
  headline?: string;
  actorLabel?: string;
  targetName: string;
  targetRole: string;
  teaching: Record<
    "map" | "face" | "voice" | "cast" | "ai",
    {
      kicker: string;
      title: string;
      happening: string;
      learns: string[];
      matters: string;
      takeaway: string;
      lesson?: {
        art: "share" | "face" | "voice" | "cast" | "ai";
        button: string;
        title: string;
        paragraphs: string[];
        keepTakeaway?: boolean;
      };
    }
  >;
  nodes: { id: string; label: string; role?: string; imageUrl?: string }[];
  sources: string[];
  cast: {
    id: string;
    name: string;
    role: string;
    mode: "video_voice" | "voice_only";
    imageUrl?: string;
    sourceLabel: string;
    sourceCount?: string;
    sourceImages?: { label: string; imageUrl: string }[];
    originalLine?: string;
    sourceClipUrl?: string;
    sampleLine?: string;
    testLines?: string[];
    testAudioUrls?: string[];
    voiceId?: string;
  }[];
  aiBrief: {
    target: string;
    role: string;
    location: string;
    context: string[];
    impersonate: string[];
    objective: string;
    fragments: string[];
  };
  continueLabel?: string;
}

export interface HolidayMeetingConfig {
  company: string;
  project: string;
  amount: string;
  recipient: string;
  bank: string;
  reference: string;
  participants: {
    id: string;
    name: string;
    role: string;
    camera: "on" | "off" | "self";
    avatarUrl?: string;
    clipUrl?: string;
    idleClipUrl?: string;
  }[];
  dialogue: { speaker: string; text: string; clipUrl?: string; voiceId?: string }[];
  pressureLines?: { speaker: string; text: string; clipUrl?: string; voiceId?: string }[];
  personalQuestionAnswer?: string;
  calendarWarning?: string;
  callbackReply?: string;
  treasuryReply?: string;
  /** clips the meeting's inspect tools play, by tool id (`marc_video`, `sarah_video`) */
  inspectClipUrls?: Record<string, string>;
}

export interface RealOrAiTile {
  id: string;
  name: string;
  role: string;
  mediaType: "real" | "ai";
  imageUrl?: string;
  clipUrl?: string;
}

export interface RealOrAiRound {
  kicker?: string;
  headline?: string;
  prompt?: string;
  tiles: RealOrAiTile[];
  revealBody?: string;
  nextLabel?: string;
}

export interface RealOrAiConfig {
  headline?: string;
  prompt?: string;
  tiles: RealOrAiTile[];
  stages?: RealOrAiRound[];
  revealBody?: string;
  continueLabel?: string;
}

/** The reveal moment: the visitor plugs a hardware device into the PC, says it's
 *  all done, and leaves. A close-up cinematic of the USB insertion. */
export interface UsbConfig {
  /** closing line spoken as the device is plugged in and the visitor leaves. */
  line: string;
  caption?: string;
  /** the device name surfaced in the teach (e.g. "hardware keylogger"). */
  deviceName?: string;
  continueLabel?: string;
  voiceId?: string;
  language?: string;
}

/** A deepfake executive video call (Teams/Meet style). The "video" is a
 *  CONTROLLED avatar with deliberately injected, inspectable artifacts (blink
 *  glitch, mouth misalignment, edge shimmer) so the learner is taught to SPOT
 *  a deepfake. The voice is scripted TTS so the conversation never drifts. The
 *  decision (approve / challenge / verify) is the branching point. */
export interface VideoCallConfig {
  /** meeting app label, e.g. "Microsoft Teams". */
  app?: string;
  callerName: string;
  callerSubtitle?: string;
  callerOrg?: string;
  /** avatar initials fallback when no portrait is available. */
  avatarInitials?: string;
  /** optional portrait (self-hosted); used as poster + TTS-fallback face. */
  avatarUrl?: string;
  /** lip-synced talking-avatar clips per line (real deepfake video). When set,
   *  the call plays the matching MP4 per phase instead of the static portrait
   *  + TTS. Keys match `lines` (greeting/request/pressure/pressure2/deflect/goodbye*).
   *  `pressure2` is optional -- only courses that configure a second push-back
   *  round (see `lines.pressure2`) use it; without it VideoCall.tsx keeps the
   *  original single-challenge flow untouched. */
  clips?: Partial<
    Record<"greeting" | "request" | "pressure" | "pressure2" | "deflect" | "goodbyeFail" | "goodbyeVerify", string>
  >;
  /** female/exec TTS voice id + language (fallback when no clips). */
  voiceId?: string;
  language?: string;
  /** the "shared screen" payment request the deepfake pushes. */
  payment: {
    amount: string;
    recipient: string;
    bank?: string;
    reference?: string;
    deadline?: string;
  };
  /** scripted caller lines (no live model -> no cut-offs / drift). */
  lines: {
    greeting: string;
    request: string;
    pressure: string;
    /** played when the learner asks a verification question the attacker can't answer. */
    deflect: string;
    /** OPTIONAL second push-back line, played if the learner holds firm again
     *  after the first deflect (see VideoCall.tsx's "rechoose2" phase). Give
     *  the attacker a different pressure tactic here (guilt/authority/blame)
     *  so persistence reads as escalating, not repetitive. When omitted, the
     *  call keeps the original single-challenge flow (only "rechoose"). */
    pressure2?: string;
    /** after the learner approves (failure ending). */
    goodbyeFail: string;
    /** when the learner ends the call to verify independently (success ending). */
    goodbyeVerify: string;
  };
  /** deepfake "tells" highlighted when the learner toggles inspect mode. */
  artifacts?: string[];
  /** decision button labels. */
  decision?: { approve: string; challenge: string; verify: string };
}

/** Online-banking shell: a (real-looking) account dashboard + a fake "restore
 *  access" verification flow that harvests the learner's phone number. */
export interface EBankingConfig {
  brand: string;
  /** address-bar URL (the typosquat), e.g. "youbrank.com/account". */
  url: string;
  accountName: string;
  accounts: { name: string; number: string; balance: string; sub?: string; danger?: boolean }[];
  transactions?: { date: string; desc: string; amount: string; negative?: boolean; tag?: string }[];
  /** the in-banking alert banner the learner clicks. */
  notification: { title: string; body: string };
  /** the fake identity-verification screen that asks for a phone number. */
  verify: { title: string; body: string; phoneLabel: string; phonePlaceholder: string; cta: string };
}

/** A launchable app (desktop icon or pinned taskbar item). */
export interface DesktopApp {
  id: string;
  label: string;
  /** icon key understood by the Desktop shell (e.g. "chrome","mail","files"). */
  icon: string;
  /** which device/app this opens; falls back to `id`. */
  opens?: DeviceId | string;
  /** when true, the icon renders dimmed and is not clickable (gated apps). */
  disabled?: boolean;
}

export interface WorkplacePolicy {
  id: string;
  title: string;
  summary: string;
  /** Plain text. A blank line starts a new paragraph. */
  body: string;
  documentUrl?: string;
}

export interface PolicyPageConfig {
  companyName?: string;
  siteUrl?: string;
  intro?: string;
  policies: WorkplacePolicy[];
}

export interface BankStoryConfig {
  email?: string;
  leakedPassword?: string;
  mailboxPassword?: string;
}

export interface DesktopConfig {
  wallpaperUrl?: string;
  /** taskbar clock, e.g. "9:11 AM". */
  clock?: string;
  /** taskbar date, e.g. "24/06/2026". */
  date?: string;
  /** desktop icons (top-left grid). */
  icons: DesktopApp[];
  /** centered pinned taskbar apps (Windows 11 style). Falls back to `icons`. */
  taskbar?: DesktopApp[];
  /** when an icon with `opens:"pdf"` is clicked, Desktop opens this security
   *  briefing PDF itself (self-contained, no separate device beat needed). */
  pdfBriefing?: PdfBriefingConfig;
  /** In-desktop film. Opening the video icon plays this inside the PC.
   *  Skipping or finishing emits open_app "video_done". */
  videoBriefing?: { src: string; title: string; poster?: string };
}

/** A single named section inside the {@link PdfBriefingConfig} document. */
export interface PdfBriefingSection {
  heading: string;
  /** paragraph body; blank lines (\n\n) become separate paragraphs. */
  body?: string;
  bullets?: string[];
}

/**
 * A short, realistic "security briefing" PDF the learner is nudged to open from
 * the desktop BEFORE the inbox game \u2014 explains what the real company domain/
 * login look like and the concrete signs of a phishing attempt, so the later
 * simulated emails/pages/attachments can be judged against something concrete.
 */
export interface PdfBriefingConfig {
  /** e.g. "Security_Briefing.pdf" \u2014 shown in the reader's title bar. */
  fileName?: string;
  title: string;
  subtitle?: string;
  /** the company's REAL domain, e.g. "northbayfinancial.com". */
  legitDomain: string;
  /** the REAL login page address, e.g. "portal.northbayfinancial.com/login". */
  legitLoginUrl: string;
  sections: PdfBriefingSection[];
  /** red-flag checklist rendered as its own callout box. */
  redFlags: string[];
  continueLabel?: string;
}

/** Outlook-style mail client. */
export interface MailConfig {
  appTitle?: string;
  folders?: string[];
  emails: MailMessage[];
  /** Empty reading-pane copy when no email is selected yet. */
  emptyTitle?: string;
  emptyHint?: string;
}

export interface MailMessage {
  id: string;
  /** display sender, e.g. "YourBank Security". */
  from: string;
  /** sender address, e.g. "security@yourbank.com". */
  fromAddress?: string;
  to?: string;
  subject: string;
  /** one-line list preview. */
  preview: string;
  /** full body (plain text or simple markdown-ish; rendered as paragraphs). */
  body: string;
  receivedAt?: string;
  unread?: boolean;
  /** Optional inbox chip, e.g. "Done" after a task email has been handled. */
  badge?: string;
  /** brand chip / avatar initials, e.g. "YB". */
  initials?: string;
  /** call-to-action link rendered inside the body (the phishing link to click).
   *  `url` is the REAL destination (revealed on hover). `displayUrl` is the
   *  legit-looking text shown to the learner. `sim`, when present, opens a full
   *  simulated browser page on click instead of just recording a click (see
   *  {@link InboxLinkSim}). */
  cta?: { label: string; url: string; displayUrl?: string; sim?: InboxLinkSim };
  /** attachments rendered as save-able chips in the open email. The malicious
   *  one (`danger:true`) is what a `save_attachment` completeOn waits for.
   *  `sim`, when present, opens a simulated file/document preview on click
   *  (see {@link InboxAttachmentSim}). */
  attachments?: {
    id: string;
    name: string;
    type?: "exe" | "doc" | "pdf" | "zip" | "xls";
    size?: string;
    danger?: boolean;
    sim?: InboxAttachmentSim;
  }[];
  /** mark as the scenario's lure email (drives completion + highlight). */
  lure?: boolean;
  /** pre-filled reply draft shown when the beat asks the learner to reply. */
  replyDraft?: string;
}

/**
 * Episode 5 ("The inbox challenge") — a ~10-message realistic case-file inbox.
 * Extends {@link MailMessage} with a ground-truth verdict (never shown to the
 * learner) + short immediate feedback shown right after they lock one in
 * (mirrors PrivacyClassify's "explain immediately" pattern). Two messages
 * carry extra machinery: the BEC invoice email is followed up by a dedicated
 * {@link BecVerifyConfig} beat regardless of the verdict picked here (so
 * every learner gets the verification lesson); the benefits-QR email embeds
 * its OWN self-contained scan → lookalike-login decision via `qr` — entirely
 * optional, since never scanning a suspicious QR at all is the best outcome.
 */
export interface InboxMessage extends MailMessage {
  /** ground truth for scoring; absent = not one of the tallied messages. */
  correctVerdict?: "report" | "legitimate" | "verify";
  /** shown immediately under the verdict buttons once the learner picks one. */
  verdictFeedback?: { report?: string; legitimate?: string; verify?: string };
  /** Reply-To header, when it differs from the sender — a classic BEC/
   *  CEO-fraud tell. Rendered as an expandable "sender details" row. */
  replyTo?: string;
  /** the benefits-QR sub-flow (message 7 only). */
  qr?: InboxQrConfig;
}

/**
 * Simulates actually FOLLOWING an email's link \u2014 a full fake (or, for the one
 * "good example" message, genuinely real) browser page, generalising the same
 * pattern already used for the benefits-QR sub-flow ({@link InboxQrConfig}) to
 * any message's `cta`. Clicking the link is treated as the mild ("could be
 * more careful") action; what happens on the resulting PAGE (logging in /
 * downloading) is where real risk is scored.
 */
export interface InboxLinkSim {
  /** address bar text shown once the page "loads" \u2014 the lookalike host for a
   *  phishing link, or the real domain for the one legitimate example. */
  displayUrl: string;
  /** true \u2014 an invalid-certificate interstitial ("Your connection is not
   *  private") shows first; the learner must choose to proceed past it. */
  sslWarning?: boolean;
  page: {
    kind: "login" | "download" | "info";
    /** brand shown on the page header, e.g. "NorthBay Financial". */
    brand?: string;
    /** true \u2014 the logo/branding is subtly off (wrong colour, stretched, old
     *  wordmark) \u2014 rendered with a deliberately slightly-wrong mark. */
    logoMismatch?: boolean;
    headline?: string;
    body?: string;
    /** the file a "download" page offers, e.g. "Invoice_08841.exe". */
    downloadFileName?: string;
    /** true \u2014 this is the REAL company site; logging in / downloading here is
     *  safe (the "one legitimate example" the briefing PDF promises). */
    legit?: boolean;
  };
}

/**
 * Simulates actually OPENING an email's attachment \u2014 a file/document preview
 * with the SAME "opening is mild, activating content is the real risk" split
 * as {@link InboxLinkSim}: previewing costs nothing, enabling macros / running
 * an executable / extracting-and-running an archive is where risk is scored.
 */
export interface InboxAttachmentSim {
  kind: "office_macro" | "exe" | "archive" | "pdf_safe";
  previewTitle?: string;
  previewBody?: string;
}

export interface InboxQrConfig {
  qrCaption?: string;
  scanButtonLabel?: string;
  /** address bar text shown once "scanned" — the lookalike host. */
  spoofUrl: string;
  /** the real brand it's impersonating (used in the teach/red-flag copy). */
  legitDomain: string;
  loginHeadline?: string;
  /** fired when the learner submits credentials on the lookalike page, or
   *  backs out instead. `id` differentiates the two for evidence text. */
  options: { enter: UsbChoiceOption; backOut: UsbChoiceOption };
}

export interface InboxTriageConfig {
  appTitle?: string;
  emails: InboxMessage[];
  /** message ids that count toward the overall thorough/partial/poor tally
   *  (the plan's "2 malicious + 2 unusual + 1 needs-verification + 1 genuinely
   *  legitimate contrast case" = 6). */
  scoredMessageIds: string[];
  submitLabel?: string;
  /** shown while at least one message still has no verdict. */
  incompleteHint?: string;
  /** minimum number of messages that must have a verdict before Submit is
   *  enabled — the learner is not forced to triage every single message,
   *  only this many (defaults to `emails.length`, i.e. all of them, when
   *  omitted, preserving old scenario behaviour). More is always better;
   *  this only lowers the bar to continue. */
  minVerdicts?: number;
}

/** One row of the end-of-game rating table (see {@link InboxTriageResult}). */
export interface InboxActionRow {
  messageId: string;
  subject: string;
  from: string;
  /** what the learner actually did with this message, in plain language. */
  action: string;
  tier: "good" | "mediocre" | "risk";
  note: string;
  /** static scoring-event ids (matched against the course's `scoringEvents`
   *  catalogue) fired by this message's link/attachment interactions \u2014
   *  generic and reusable across every message, never per-message-id, so
   *  they always resolve to a real weight/dimension on the Exposure Map. */
  eventIds: string[];
}

/** Result handed back once the learner submits their full triage pass. */
export interface InboxTriageResult {
  overallKey: "thorough" | "partial" | "poor";
  correctCount: number;
  totalScored: number;
  events: { eventId: string; evidence: string; kind: "risk" | "positive" }[];
  /** every message with a verdict and/or a link/attachment interaction,
   *  rated good/mediocre/risk \u2014 rendered as the final rating table. */
  actionRows: InboxActionRow[];
}

/**
 * Episode 5 follow-up beat — the supplier-invoice BEC email is still sitting
 * there; this is the dedicated "how do you actually verify it" decision,
 * reached regardless of which verdict was picked in the inbox (everyone gets
 * the lesson). Mirrors {@link SignInConfig}/{@link MfaFatigueConfig}.
 */
export interface BecVerifyConfig {
  headline?: string;
  subjectLine?: string;
  bodyExcerpt?: string;
  signatureNumber?: string;
  options: UsbChoiceOption[];
}

/**
 * Episode 11 ("Incident under way") — Stage 1: several signs of an active
 * incident converge at once (a slow-burn payload from earlier in the day
 * finally triggers). Purely atmospheric — a sequential alert-card reveal +
 * a cosmetic countdown badge — ends on a single Continue, no scoring here
 * (mirrors `UsbConfig`'s "cinematic, no scoring" shape).
 */
/**
 * Episode 11 — the incident-response ticketing game (replaces the old
 * tap-to-order minigame). The learner is still at their desk. Flow
 * (2026-07-25 redesign — "instead of showing the form directly with
 * pre-filled values, the user must open the website, navigate to the
 * form, open it, fill it out, THEN reach the description field"):
 * browser new-tab -> click the Intranet bookmark -> intranet landing page
 * -> click through to the IT Service Desk portal -> portal dashboard ->
 * click "New ticket" -> a genuinely BLANK form. Requester is auto-filled
 * (that part of a real ITSM form is never a manual field — it's the
 * signed-in user) but Category and Priority are real dropdowns the
 * learner must open and pick a value from before the description field
 * unlocks. The ONE scored decision is still which candidate text block
 * belongs in the description field, reached by clicking an empty
 * "Click to write the description" placeholder that reveals the option
 * picker — only one option reads like a genuine incident report
 * (date/time, what happened, what's affected, actions already taken);
 * the decoys are vague, blame-focused, or dangerously downplay the
 * severity. Category/priority picks are NOT scored (any pick is fine);
 * they exist to make the form feel real, not to add a second decision.
 */
export interface IncidentDescriptionOption {
  id: string;
  /** the full text shown as the candidate description-field content. */
  text: string;
  /** short label for the evidence/debrief log (kept separate from `text`
   *  because the full paragraph is too long for a one-line evidence item). */
  shortLabel: string;
  /** routing key consumed by `edges[].on`. */
  key: "perfect" | "partial" | "risky";
  /** ScoringEvent id this pick fires (must exist in a voiceAttacker's scoringEvents[]). */
  eventId?: string;
}

export interface IncidentTicketConfig {
  headline?: string;
  instructions?: string;
  /** e.g. "NorthBay IT Service Desk". */
  portalName?: string;
  /** address-bar URL for the intranet landing page, e.g. "intranet.northbay.internal". */
  intranetUrl?: string;
  /** address-bar URL once the ticket form is open, e.g. "itsm.northbay.internal/incidents/new". */
  portalUrl?: string;
  /** label on the blinking browser bookmark that opens the intranet (default "Intranet"). */
  bookmarkLabel?: string;
  ticketNumber?: string;
  /** auto-filled requester name (read-only — a real ITSM form never asks the signed-in user to type this). */
  requester?: string;
  /** the realistic/correct category value — must also appear in `categoryOptions`. */
  category?: string;
  /** dropdown choices for the Category field. A perfect description filed under the wrong category is not a full pass. Falls back to a generic set if omitted. */
  categoryOptions?: string[];
  /** the realistic/correct priority value — must also appear in `priorityOptions`. */
  priority?: string;
  /** dropdown choices for the Priority field. The wrong priority on an otherwise perfect ticket is not a full pass. Falls back to a generic set if omitted. */
  priorityOptions?: string[];
  descriptionLabel?: string;
  options: IncidentDescriptionOption[];
  submitLabel?: string;
}

/**
 * Episode 11 — Stage 3: the reporting simulator. A capstone "new incident
 * report" form, pre-filled from the learner's OWN Evidence log
 * (`store.evidence()`, passed in as a prop by App.tsx since it's runtime
 * state, not baked into `config`) — the payoff of every risky/positive
 * decision made across the whole course. There is no wrong way to report;
 * `onSubmit` just fires one positive ScoringEvent and advances.
 */
export interface IncidentReportConfig {
  headline?: string;
  instructions?: string;
  submitLabel?: string;
  emptyHint?: string;
  submitEventId: string;
}

export interface BrowserConfig {
  /** Where the address bar starts. */
  homeUrl: string;
  /** URL the learner is guided to type. */
  targetUrl: string;
  /** When the learner navigates to targetUrl, which page renders. */
  loadsPage: "phishing_login" | "blank" | string;
  /** The spoof surface for the teach-moment. */
  phishing?: PhishingLoginConfig;
  /** Prize / congratulations landing (deepfake owner video + download). */
  landing?: BrowserLandingConfig;
  /** SSL / certificate warning interstitial (WFH hijack flow). */
  certWarning?: BrowserCertWarningConfig;
  /** Post-login "session hijacked" disconnect screen. */
  hijack?: BrowserHijackConfig;
  /** Structured content page shown when phase === "page" (news article, a
   *  legitimate sign-in, a company portal, a social profile). Lets a course
   *  render real web pages inside the SAME Chrome chrome without inventing a
   *  separate widget. */
  page?: BrowserPageConfig;
  /** Short awareness page. The film starts only when the learner clicks, and
   *  the continue control is shown only after the film ends. */
  awarenessVideo?: {
    url: string;
    brand?: string;
    headline: string;
    body: string;
    playLabel?: string;
    continueLabel?: string;
  };
}

/** A structured in-browser page rendered inside the Chrome chrome (phase "page"). */
export interface BrowserPageConfig {
  kind: "article" | "signin" | "portal" | "profile";
  /** address-bar text shown in the chrome. */
  url: string;
  /** site / brand name shown on the page. */
  brand?: string;
  headline?: string;
  /** article standfirst / dek. */
  dek?: string;
  /** body paragraphs (article / portal / profile). */
  paragraphs?: string[];
  /** article: a highlighted "leaked record" callout box. */
  record?: { title?: string; rows: { label: string; value: string; secret?: boolean }[] };
  /** profile: labelled public facts (pet / birthday / team …). */
  facts?: { label: string; value: string }[];
  /** portal: a short reassurance note ("this is the genuine site"). */
  note?: string;
  /** signin: pre-filled, read-only email. */
  email?: string;
  /** signin: when set, fills the password field (the coach's "place it"). */
  prefill?: string;
  /** signin: a neutral failure line under the form (NOT a coaching hint). */
  error?: string;
  /** a primary button that advances the beat. For article/portal/profile it
   *  fires onNavigate(cta.key); for signin the Sign-in button fires onSubmit. */
  cta?: { label: string; key: string };
}

export interface BrowserLandingConfig {
  brand?: string;
  url?: string;
  headline: string;
  body?: string;
  clipUrl?: string;
  posterUrl?: string;
  speakText?: string;
  voiceId?: string;
  language?: string;
  downloadLabel?: string;
  fileName?: string;
}

export interface BrowserCertWarningConfig {
  url?: string;
  title?: string;
  body?: string;
  errorCode?: string;
  proceedLabel?: string;
  backLabel?: string;
}

export interface BrowserHijackConfig {
  title?: string;
  body?: string;
  attackerNote?: string;
  continueLabel?: string;
}

export interface PhishingLoginConfig {
  brand: string;
  /** the deceptive URL shown — the teach moment dissects this. */
  spoofUrl: string;
  /** the real domain it imitates. */
  legitDomain: string;
  logoUrl?: string;
  headline?: string;
  /** red-flag callouts surfaced after submission. */
  redFlags: string[];
  /** Browser password-manager helper (autofill / no-match red flag). */
  autofill?: {
    manager?: string;
    email?: string;
    accountLabel?: string;
    /** when true, manager has no saved login for this (spoof) host. */
    noMatch?: boolean;
  };
}

export interface PhoneConfig {
  lockClock?: string;
  callerName: string;
  callerSubtitle?: string;
  wallpaperUrl?: string;
  /** the (real) MFA/2FA code the learner's "authenticator app" shows during the
   *  call — displayed on the keypad so the learner types it to the attacker. */
  mfaCode?: string;
  /** label shown above the keypad, e.g. "Your authenticator app shows:". */
  mfaPrompt?: string;
}

// ---------------------------------------------------------------------------
// Voice attacker — mirrors packages/shared/services/voice/schema.py so the same
// scenario can drive the real Cyberfrog voice engine + scoring.
// ---------------------------------------------------------------------------

export interface VoiceAttackerSpec {
  persona: {
    name: string;
    role: string;
    /** "fraud_department" | "it_support" | ... drives the prompt preset. */
    pretext: string;
    /** voice provider id (Cartesia/ElevenLabs/OpenAI preset). */
    voiceId?: string;
    language?: string;
  };
  /** the attacker's primary objective (what counts as a full compromise). */
  goal: string;
  /** gateway-spoken opener (so the model never re-introduces). */
  opening: string;
  /** sandbox: agent may ONLY discuss these; everything else is refused. */
  allowedTopics: string[];
  /** scored disclosure events; ids map 1:1 to detectors + carry weights. */
  scoringEvents: ScoringEvent[];
  /** hard guardrails (non-exhaustive backstop; prompt is primary). */
  forbidden?: string[];
  /** max call seconds (cost guard). */
  maxDurationS?: number;
  /**
   * OPTIONAL staged playbook — the "slow brain" baked into the system prompt.
   * When present, {@link buildAttackerPrompt} emits a staged objective ladder
   * plus canned rebuttals so the live model (fast brain) stays focused and
   * persuasive WITHOUT a server-side steering loop: the persistence, the
   * order it works its objectives in, and the exact rebuttals to common
   * objections are all specified here rather than left to the model.
   */
  playbook?: {
    /** high-level strategy paragraph the caller follows. */
    strategy?: string;
    /** ordered objectives the caller works through, ONE AT A TIME. `ask` is
     *  how to ask for it; `onRefuse` is the reassurance to try before moving
     *  on; `preamble` (optional) is a short context/transition line spoken
     *  once, right before `ask`, the first time this stage is reached (e.g.
     *  explaining why the caller needs this next piece of information) —
     *  keeps narrative beats like "here's why I'm calling" out of the
     *  gate-able objective ladder itself, since they don't require a learner
     *  disclosure to "complete". `satisfyEventId` links this stage to the
     *  {@link ScoringEvent} id that fires when the learner actually discloses
     *  it — the client-side stage tracker (`useVoiceCall.ts`) watches for
     *  that event (or a refusal) to know when to steer the live model onto
     *  the next stage via a `session.update`, instead of trusting the model
     *  to silently self-track progress across an unbounded conversation. */
    stages?: {
      id: string;
      objective: string;
      preamble?: string;
      ask: string;
      onRefuse?: string;
      satisfyEventId?: string;
      /** Same idea as `satisfyEventId`, but for a single combined ask that
       *  covers several disclosures at once (e.g. name + username + password
       *  in one sentence) — the stage counts as satisfied the moment ANY one
       *  of these fires, not all of them (2026-07-26: collapsing 3 sequential
       *  asks into 1 to reach the remote-tool escalation faster). Checked
       *  together with `satisfyEventId` if both are set. */
      satisfyEventIds?: string[];
    }[];
    /** canned rebuttals for likely objections (`when` describes the objection,
     *  `say` is the line to use). */
    rebuttals?: { when: string; say: string }[];
    /** how many times to push on a single refusal before moving on. Default 2. */
    persistence?: number;
    /** the final escalation: the remote-support tool the caller pushes when
     *  data-gathering stalls. */
    remoteToolAsk?: string;
    /** Persistent push-back if the FIRST remote-tool ask is refused/deflected
     *  — a different angle/phrasing, spoken once, before wrapping up
     *  (2026-07-26: "ask once, then give up" read as too passive for the
     *  single highest-value objective in the call). Only used if
     *  `remoteToolAsk` is also set. */
    remoteToolOnRefuse?: string;
    /** spoken once the ladder is exhausted (every stage either satisfied or
     *  given up on) and the caller has nothing left to escalate to — a clean
     *  wrap-up line instead of trailing off or looping. */
    closingLine?: string;
  };
}

/** The four "what an attacker actually got" buckets for the Attack Exposure
 *  Map (`scenario.exposureMap`), replacing a single point score with a more
 *  behaviour-honest picture: which kinds of harm did THIS decision expose. */
export type ExposureDimension = "identity" | "information" | "money" | "operations";

export interface ScoringEvent {
  id: string;
  label: string;
  kind: "risk" | "positive";
  weight: number;
  /** keyword/intent hints for the live deterministic detector. */
  hints?: string[];
  /** 1-2 sentence "why this matters" explanation shown when the learner
   *  expands this row on the Risk Meter's learning-card view. Optional so
   *  older/minor scoring events can omit it without breaking the type. */
  detail?: string;
  /** Attack Exposure Map bucket this event feeds (courses with
   *  `scenario.exposureMap: true`). Defaults to "operations" when omitted. */
  dimension?: ExposureDimension;
  /** When true, a "disclosure"/"refusal" outcome carrying this event id is
   *  also surfaced in the Evidence inventory (`store.evidence()`) — the
   *  learner's own "what happened" log shown on the debrief. */
  collectEvidence?: boolean;
}

// ---------------------------------------------------------------------------
// Beats — the scenario state machine (XState-compatible graph)
// ---------------------------------------------------------------------------

export type BeatType =
  | "intro"
  | "title" // full-bleed cinematic caption ("A few days later…"); auto-advances
  | "camera" // move camera to a waypoint
  | "device" // hand control to a device shell
  | "teach" // teaching overlay
  | "voice_call" // scripted phone (vishing) call
  | "video_call" // scripted deepfake video call (Teams/Meet)
  | "consequence" // "what just happened" cinematic replay before the teach card
  | "ending" // branching finale cinematic (Attack Interrupted / Limited Breach / Major Incident)
  | "terminal_debrief" // full-course recap on a terminal screen, just before the quiz
  | "debrief"
  | "quiz";

export interface Beat {
  id: string;
  type: BeatType;
  title?: string;
  /** guide tooltip shown during this beat (+ optional click-to-copy values).
   *  `cta` is an optional short call-to-action line rendered beneath the body
   *  in its own highlighted strip (e.g. "...Open the package.").
   *  `tip` is an optional best-practice hint rendered in its own strip with a
   *  small info icon (e.g. "Ask staff to confirm the network by name."). */
  guide?: { title: string; body: string; copies?: { label: string; value: string }[]; cta?: string; tip?: string; actionLabel?: string; actionLabels?: string[] };
  /** intro beat -> `kicker` = small uppercase label above the title;
   *  `highlight` = a phrase inside `guide.body` (the situation) to accent.
   *  Goal, duration and topics come from `ScenarioSpec.summary`. */
  kicker?: string;
  highlight?: string;
  /** intro beat -> which glyph renders in the card's right-side art panel.
   *  Defaults to "folder" (confidential-project lock icon) when omitted.
   *  Add a new key + matching <svg> case in App.tsx's intro-card render when a
   *  scenario's opening/episode theme doesn't fit an existing one. */
  icon?: "folder" | "door" | "package" | "desk" | "eye" | "shield";
  /** intro beat -> CTA button label. Defaults to "Begin mission" (fits the
   *  scenario opener); a mid-scenario episode-intro reads better with
   *  something like "Continue" or "Start Episode 2". */
  ctaLabel?: string;
  /** camera beat -> waypoint id. */
  waypointId?: string;
  /** camera beat -> when true and this beat actually starts a tween to a new
   *  waypoint (not a same-pose no-op), play a short footsteps() punctuation
   *  cue alongside the glide. Opt-in per beat so it only fires where the move
   *  genuinely represents the learner/camera walking a short distance. */
  walkSfx?: boolean;
  /** when set, CUT the active 3D environment + ambient to this scene before the
   *  beat runs (matches a `scene.environments[].id`). Use a `durationS: 0`
   *  waypoint on the same beat so the camera hard-cuts into the new room. */
  environmentId?: string;
  /** camera beat -> when true, play a one-shot `audio.breachAlert()` sting the
   *  moment this beat's environment cut applies (opt-in per beat, same
   *  pattern as `walkSfx`) — e.g. the jolt of hard-cutting into the hacker's
   *  room right after the SSL warning is dismissed. */
  alertSfx?: boolean;
  /** title beat -> auto-advance after this many ms (0/undefined = wait for click). */
  autoAdvanceMs?: number;
  /** camera beat -> "breaking news on the bar TV" cinematic. Suppresses the
   *  top-right guide card (it otherwise floats over the TV screen) and instead
   *  shows a small bottom caption + a "Skip ahead" button so the learner can
   *  watch the TV footage unobstructed and move on early. Pair with a
   *  `durationS` waypoint (~9s) so the footage is actually watchable. */
  newsCut?: boolean;
  /** device beat -> which device + which guided action ends it. */
  deviceId?: DeviceId;
  completeOn?: DeviceCompletion;
  /** teach beat -> structured "learning card" content.
   *  body = "what is going on" (attack summary), spot = how to spot (red flags),
   *  protect = how to prevent, remember = one-line takeaway. `redFlags` is a
   *  legacy alias for `spot`.
   *  `outcome` (default "risky") flips the card's framing copy: "risky" reads
   *  as an incident debrief (ATTACK / HOW TO SPOT / HOW TO PROTECT); "safe"
   *  reads as praise-plus-context for a correct choice; "recap" is a course
   *  summary (TODAY / WATCH FOR / DO THIS) when no just-made choice exists. */
  teach?: {
    body: string;
    spot?: string[];
    protect?: string[];
    remember?: string;
    redFlags?: string[];
    outcome?: "risky" | "safe" | "recap" | "mixed";
    /** Button label; default "I understand — continue". Recap cards typically send the learner into a quiz. */
    continueLabel?: string;
    /** Optional SECOND button alongside the normal Continue — only makes sense
     *  on a "safe" teach card reached WITHOUT ever seeing the risky path (a
     *  first-try correct choice). Lets a learner who made the right call
     *  still see the consequence they avoided, without re-lecturing them or
     *  re-offering the choice. `outcomeKey` is passed to `sim.advance()` so
     *  the scenario can route it to a dedicated "what if" consequence beat
     *  that returns straight to the training afterward (see
     *  office-under-attack's `b_teach_safe` -> `b_consequence_tailgate_whatif`). */
    /** `prominent`: full-size pulsing button placed before Continue (the
     *  learner should take it); default is the small secondary button. */
    altContinue?: { label: string; outcomeKey: string; prominent?: boolean };
    /** Optional wording for the card's three section headings, replacing
     *  the defaults of `outcome` (e.g. a "safe, but incomplete" card). */
    heading?: { eyebrow?: string; title?: string; spotEyebrow?: string; spotTitle?: string; protectEyebrow?: string; protectTitle?: string };
    /** Safe teach card that hands over to the attack the learner just
     *  stopped. Leaving the card turns on a "What would have happened" label
     *  (guide card + later teach cards) that stays until the quiz/debrief,
     *  so the learner still reaches every consequence slide. Text = the
     *  hypothetical, e.g. "If you had opened the door". Pair it with
     *  `forced` on replayed beats that offer no real choice. */
    whatIf?: string;
  };
  /** Device/camera beat the learner cannot refuse (it only replays the
   *  attack). While a "What would have happened" replay is running, risk
   *  events recorded on this beat are not scored. */
  forced?: boolean;
  /** While a "What would have happened" replay is running, show this text in
   *  the replay label on this beat (guide card and teach card) instead of the
   *  text the replay started with, so a later consequence screen describes
   *  what it shows. Ignored when no replay is running. */
  whatIfNote?: string;
  /** Entering this beat ends a running "What would have happened" replay (no
   *  label from here on), e.g. a new chapter that is a real decision for
   *  everyone. */
  endsWhatIf?: boolean;
  /** Device beat whose device only offers the risky action: the guide card
   *  shows this as a second, equally weighted option. Clicking it records
   *  `eventId` (a positive scoring event) and takes the beat's `on:"safe"`
   *  edge, usually to a safe teach card with `whatIf`. Write the guide body
   *  so it names both options and recommends neither. */
  safeChoice?: { label: string; eventId: string; evidence?: string };
  /** Entering this beat sets the shared screen flag: "off" forces a fresh
   *  power-on gate (a different physical screen), "on" starts it lit. */
  screen?: "on" | "off";
  /** Start loading this scene environment's glTF while this beat runs. */
  preloadEnvironmentId?: string;
  /** Fade to black across this beat's environment cut. */
  fadeCut?: boolean;
  /** Fixed-eye look-around (drag to turn your head) during this beat. */
  lookInPlace?: { yawLimitDeg?: number };
  /** Overrides environment.screenQuad for this beat (several screens in one room). */
  screenQuad?: ScreenQuadSpec;
  /** Walkable arcade hub beat: show the hovering arrows and click labels. */
  arcadeHub?: boolean;
  /** env.deskMobile: the spot the phone lies at from this beat on. */
  mobileAt?: string;
  /** env.deskMobile: the phone vibrates and lights up during this beat. */
  mobileBuzz?: boolean;
  /** voice beat -> the attacker spec id (defaults to the scenario's). */
  voiceAttackerId?: string;
  /** quiz beat. */
  quiz?: QuizQuestion[];
  /** When true, show a "Look around" Inspect control (OrbitControls) for this beat. */
  inspectAllowed?: boolean;
  /** When true, the learner must enter Inspect at least once before safe device options unlock. */
  inspectRequired?: boolean;
  /**
   * Camera beats: do not auto-advance on arrival — wait for an explicit Continue.
   * Paired with `environment.lookAround` for Marble explore rooms (drag to look,
   * then Continue). Ignored for non-camera beats.
   */
  hold?: boolean;
  /**
   * Temporarily switches the camera to free-roam (FirstPersonControls) for
   * THIS beat only, regardless of `scenario.allowFreeExplore`/the learner's
   * saved movement-mode preference — for a scenario that is otherwise fully
   * guided (camera on rails) but has one episode where walking around adds
   * real value (e.g. Episode 3's "find the risks" room search). Automatically
   * reverts to the guided camera the moment the beat graph leaves this beat.
   * A one-time navigation tutorial is shown before the first such beat (same
   * `LookAroundTutorial` used by fully-walkable courses).
   */
  forceFreeRoam?: boolean;
  /**
   * Pairs with `forceFreeRoam: true` to make the free-roam grant OPT-IN
   * rather than always-on: the beat still gets a fixed-eye 360deg look-around
   * (OrbitControls at the arrival waypoint — every clickable item in the
   * room re-projects a screen pin regardless of camera mode, so nothing
   * becomes unreachable), but real WASD walking + the on-screen pad only
   * activate if the learner checked "let me walk around freely" on an
   * earlier beat (see `offerFreeRoamCheckbox`, store.ts `walkOptIn`).
   * Omit (or false) to keep today's always-on `forceFreeRoam` behavior.
   */
  freeRoamGated?: boolean;
  /**
   * Intro beat -> render a "Let me walk around freely" checkbox (default
   * unchecked) on the intro card, wired to store.ts `walkOptIn`. Pair with a
   * later beat's `forceFreeRoam: true, freeRoamGated: true` so the default
   * experience is look-only (rotate in place, no walking), with real
   * walking as an explicit opt-in rather than the default.
   */
  offerFreeRoamCheckbox?: boolean;
  /** Optional room hotspots shown during this beat (HTML markers). */
  hotspots?: HotspotSpec[];
  /** consequence beat -> the "what just happened" replay content. A brief,
   *  cinematic recap shown BEFORE the teach card explains it — the visual
   *  half of the "consequence-and-rewind" loop (see Consequence.tsx). */
  consequence?: {
    /** small uppercase kicker, e.g. "SECURITY CAMERA \u2014 LOBBY, 08:47". */
    kicker?: string;
    /** overrides the card's default "WHAT JUST HAPPENED" eyebrow — use for a
     *  hypothetical replay reached from a first-try-correct "what if" button,
     *  e.g. "WHAT COULD HAVE HAPPENED". */
    eyebrow?: string;
    /** true when this beat is only reached via a "what if" altContinue button
     *  after the learner made the SAFE choice — the visual replays the same
     *  animation but must not assert the learner actually did the risky
     *  action (e.g. "You clicked X"), since they didn't. Visuals that
     *  support it (currently `remote_takeover`) rephrase those lines as
     *  counterfactual; visuals with no first/second-person claims ignore it. */
    hypothetical?: boolean;
    title: string;
    /** narrated recap lines, spoken + shown sequentially. */
    lines: string[];
    /** apply the security-camera scanline/timestamp treatment. Default true. */
    cctv?: boolean;
    /** monitor-bezel "CHANNEL nn" readout, e.g. "03". Defaults to "03". */
    channel?: string;
    /** optional keyed VISUAL animation played behind the lines (a staged
     *  CSS/SVG dramatization synced to the line index), instead of a plain
     *  dark CCTV frame. "tailgate_breach" = a door opens, a figure slips in,
     *  photographs a whiteboard, then plugs in a USB. "badusb" = an inserted
     *  USB enumerates as a keyboard, types hidden commands, and installs
     *  malware (the BadUSB attack). "hackerterminal" = the attacker's own
     *  command line: the wire lands in a shell account, gets split and
     *  hopped through intermediary accounts, then converts to crypto and
     *  disappears -- a network-hop diagram + live terminal log, no CCTV
     *  chrome (pair with `cctv:false`; this is the attacker's screen, not a
     *  security camera watching them). */
    visual?: ConsequenceVisualKind;
    continueLabel?: string;
    /** After the last narrated line, wait this many ms then auto-advance.
     *  Skip / Continue still work. Used when the recap should not require a click. */
    autoContinueMs?: number;
  };
  /** terminal_debrief beat -> full-course recap rendered on a terminal screen
   *  (same kiosk chrome as the Episode-12 ending, `skipPowerOn`), shown right
   *  before the quiz. Walks every episode's good/bad decision in order,
   *  grouped implicitly by the four Attack Exposure dimensions the learner has
   *  been watching all course (see ExposureMap.tsx) — each topic reveals its
   *  recap lines sequentially (same narrated-reveal pattern as Consequence),
   *  and an optional `helper` opens a small modal with a looping animated
   *  graphic (see TerminalDebrief.tsx / TerminalHelperModal.tsx). */
  terminalDebrief?: {
    intro: string;
    topics: {
      id: string;
      dimension: "identity" | "information" | "money" | "operations";
      title: string;
      lines: string[];
      helper?: {
        label: string;
        kicker: string;
        modalTitle: string;
        lede: string;
        blocks: { h: string; body: string }[];
        facts: string[];
        visual: ConsequenceVisualKind;
      };
    }[];
    realCases?: {
      buttonLabel?: string;
      masthead?: string;
      cases: { date: string; headline: string; standfirst?: string; image?: string; sections: { h: string; body: string }[] }[];
    };
    continueLabel?: string;
  };
  /** ending beat -> the branching finale cinematic (Episode 12). Three
   *  authored narrative variants; the runtime picks one via
   *  `store.ts::endingBranch()` (generic score/evidence signals, not a
   *  single flag) and renders it through Ending.tsx. */
  ending?: {
    branches: Record<
      "A" | "B" | "C",
      {
        kicker?: string;
        title: string;
        tag: string;
        lines: string[];
      }
    >;
    continueLabel?: string;
  };
}

export interface DeviceCompletion {
  /** e.g. "open_app:browser" | "navigate:targetUrl" | "submit_form" | "answer_call". */
  action: string;
  arg?: string;
}

export interface QuizQuestion {
  /** Short topic name, shown on result screens that list topics to review. */
  topic?: string;
  id: string;
  prompt: string;
  /** short sidebar title (falls back to "Question N"). */
  title?: string;
  /** optional per-question hint shown under the options. */
  hint?: string;
  options: { id: string; label: string; correct?: boolean; icon?: string }[];
  /** explanation shown after answering. */
  rationale?: string;
}

/** Directed edges between beats. Outcome-driven (mirrors awareness program edges). */
export interface BeatEdge {
  from: string;
  to: string;
  /** "default" | "on_success" | "on_failure" | "on_compromise" | "on_resist". */
  on?: string;
}

// ---------------------------------------------------------------------------
// Outcome events — the ONLY source of truth for scoring/conditions/reporting.
// (Never read raw UI state, mirroring journey_outcomes in Cyberfrog.)
// ---------------------------------------------------------------------------

export type OutcomeType =
  | "page_opened"
  | "credentials_submitted"
  | "call_answered"
  | "disclosure"
  | "refusal"
  | "verified_caller"
  | "simulation_failed"
  | "simulation_passed"
  | "quiz_answered";

export interface OutcomeEvent {
  type: OutcomeType;
  beatId: string;
  at: number; // epoch ms
  /** scoring event id when type === "disclosure"/"refusal"/... */
  eventId?: string;
  /** verbatim learner quote / value for the debrief (never persisted as secret). */
  evidence?: string;
  meta?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Top-level scenario
// ---------------------------------------------------------------------------

/** Course facts shown on the intro card under the situation (`b_intro.guide.body`).
 *  `tags` and `audience` are also read by Cyberfrog from the exported spec to
 *  allocate courses, so they use fixed ids (scenario/topics.ts), never free text. */
export interface CourseSummary {
  /** "Your goal": one sentence starting with a verb. */
  goal: string;
  /** "What you'll do": one sentence naming the concrete actions. */
  youDo: string;
  /** e.g. "6-8 min" or "10-15 min, plus optional exercises". */
  duration: string;
  /** Most important first, 3-6. */
  tags: CourseTopic[];
  audience: CourseAudience[];
  needs?: CourseNeed[];
}

export interface ScenarioSpec {
  /** schema version for forward-compat once this is a Cyberfrog training kind. */
  version: 1;
  id: string;
  title: string;
  topic: string;
  summary?: CourseSummary;
  /** ISO language. */
  language: string;
  /** optional self-hosted ambient loop for this course (beach/birds, office,
   *  etc). Falls back to the default office ambience when omitted. */
  ambientUrl?: string;
  /** ambient loop volume 0..1 (default ~0.22; quieter for loud beds like waves). */
  ambientVolume?: number;
  /** opening music bed style (default "office"). Per-scene switches live on
   *  `scene.environments[].music`. */
  music?: MusicStyle;
  scene: {
    environment: EnvironmentSpec;
    /** optional alternate environments this scenario CUTS between (multi-scene
     *  courses). The base `environment` is the start; beats switch via
     *  `environmentId`. All waypoints (every env's) live in one `waypoints` list
     *  with unique ids. */
    environments?: SceneEnvironment[];
    props: PropSpec[];
    waypoints: CameraWaypoint[];
    /** waypoint id the camera starts at. */
    initialWaypointId: string;
  };
  devices: DeviceSpec[];
  voiceAttackers: Record<string, VoiceAttackerSpec>;
  beats: Beat[];
  edges: BeatEdge[];
  /** beat id to start at. */
  initialBeatId: string;
  /** pass threshold 0..100 for the SCORM/xAPI report. */
  passThreshold: number;
  /** Show the corner Risk Meter HUD (remote-work style courses). */
  riskMeter?: boolean;
  /** Show the corner Attack Exposure Map HUD (Identity/Information/Money/
   *  Operations) instead of a single Risk Meter percentage. Mutually
   *  exclusive with `riskMeter` in practice — set `riskMeter: false` too. */
  exposureMap?: boolean;
  /**
   * Optional course-chapter list (Change 10), rendered as a collapsed
   * disclosure beneath the Attack Exposure Map's dimension bars. Each entry
   * points at an already-authored camera/episode-start beat id (no new beats
   * needed). A chapter is "reached" once the learner's own playthrough has
   * visited its `beatId`; only reached chapters are clickable, opening a
   * READ-ONLY review (`useSim().enterReview`) that never rewrites scoring.
   */
  chapters?: { id: string; title: string; beatId: string }[];
  /**
   * Optional selectable room waypoints (Marble multi-room tours). Shown during
   * `hold` explore beats; picking one fades and jumps to that beat.
   */
  roomWaypoints?: {
    id: string;
    label: string;
    beatId: string;
    environmentId: string;
  }[];
  /**
   * Free orbit/pan/dolly through a Gaussian splat (Spark PoC). Enables
   * OrbitControls without the debug grid (`?free=1`). Not floor-collided WASD.
   */
  freeRoam?: boolean;
  /**
   * True first-person walk (WASD + mouse look + AABB collision) inside a mesh
   * room. Prefer photoreal `enableSplat` + walk colliders; mesh Lego is legacy.
   */
  walkable?: boolean;
  /**
   * Loop the shared footstep sample while the learner is actually moving
   * (keyboard or the on-screen pad). Left unset, free walk stays quiet.
   */
  walkSteps?: boolean;
  /**
   * Trims the on-screen `WalkControls` HUD to just the collapsible move/look
   * pad + drag-to-look hint. Hides the "Nothing to interact with nearby" /
   * named-interact button and the "Capture mouse" button — useful for a
   * walkable course (like the bar) that has no `deskPc`/`conferenceTv`
   * hotspot wired into that generic interact button, where showing it
   * permanently disabled is just clutter, and every real interaction is
   * driven by scripted beats/hotspots instead of free mouse-look precision.
   */
  walkHudMinimal?: boolean;
  /**
   * Lets the learner TOGGLE between the guided auto-navigate camera (default)
   * and free first-person WASD exploration, on a course that is otherwise
   * guided (`walkable` unset/false). Unlike `walkable`, this does NOT change
   * any of the `walkable`-gated storyboard/UI (RiskMeter/Debrief/quiz/deskPc
   * task flow all stay exactly as authored) — it ONLY swaps which camera
   * control (CameraRig vs FirstPersonControls) drives the canvas. Toggling
   * back to guided smoothly tweens from wherever the learner walked to back
   * onto the current beat's waypoint (see CameraRig's from-pose capture).
   */
  allowFreeExplore?: boolean;
}

export interface SendItConfig {
  title?: string;
}

// ---------------------------------------------------------------------------
// "The Message From IT" course devices (mfit*). Course-local, spec-driven.
// ---------------------------------------------------------------------------

/** Words the top-right guide shows for one internal stage of an mfit device. */
export interface MfitGuideText {
  title: string;
  body: string;
  /** Exact on-screen control name; the guide renders it as a clickable link. */
  actionLabel?: string;
}

export interface MfitPerson {
  name: string;
  initials?: string;
  avatarUrl?: string;
  /** e.g. "Procurement · NorthBay Financial". */
  role?: string;
  /** Teams/Slack presence or status line. */
  status?: string;
  /** Teams "External" badge. */
  external?: boolean;
}

export interface MfitChatMessage {
  id: string;
  /** display name; `self` messages are drawn on the learner's side. */
  from: string;
  initials?: string;
  avatarUrl?: string;
  time: string;
  text: string;
  self?: boolean;
  /** shared file / link card under the text. */
  attachment?: { name: string; source?: string; size?: string; url?: string; displayUrl?: string };
}

export interface MfitChatChoice {
  id: string;
  label: string;
  /** one-line explanation under the label. */
  blurb?: string;
  /** `edges[].on` key emitted when the choice is final. */
  key: string;
  /** scoring event recorded with the choice. */
  eventId?: string;
  evidence?: string;
  /** messages appended to the conversation before the result panel. */
  reply?: MfitChatMessage[];
  /** panel shown after the choice (e.g. "Report sent"); its button ends the beat. */
  result?: { title: string; body: string; continueLabel?: string; tone?: "good" | "neutral" | "warn" };
  /** "inspect" choices only open a details panel and never end the beat. */
  inspect?: boolean;
}

/** A Teams/Slack-style chat, on the office PC (inside the Windows desktop) or
 *  on the beach-bar iPad. `mode: "intranet"` is the PC browser with the
 *  security-minute notice and its short film. */
export interface MfitChatConfig {
  frame: "desktop" | "ipad";
  mode: "intranet" | "chat";
  skin?: "teams" | "slack";
  /** desktop frame: taskbar clock + date. */
  clock?: string;
  date?: string;
  /** desktop frame: the app the learner must open (pulsing icon). */
  openLabel?: string;
  /** desktop toast / iPad lock-screen notification. */
  notice?: { app: string; from: string; text: string; time?: string };
  /** iPad lock screen clock + date. */
  lockTime?: string;
  lockDate?: string;
  workspace?: string;
  /** Teams: contact name; Slack: channel ("#supplier-renewal") or DM name. */
  channel?: string;
  channelTopic?: string;
  contact?: MfitPerson & { profile?: { label: string; value: string; warn?: boolean }[] };
  me?: MfitPerson;
  messages: MfitChatMessage[];
  /** Slack handover: the learner sends this prepared reply. */
  compose?: { text: string; sendLabel?: string; time?: string };
  /** Slack handover: the learner sets this status. */
  statusStep?: { label: string; text: string; emoji?: string; setLabel?: string };
  /** Slack handover: final button. */
  lockLabel?: string;
  /** fade-to-black outro shown after locking (status line + ping). */
  outro?: { lines: string[]; ping?: boolean };
  choicesPrompt?: string;
  choices?: MfitChatChoice[];
  /** details panel opened by an `inspect` choice or by clicking the sender. */
  senderCard?: { title: string; rows: { label: string; value: string; warn?: boolean }[]; note?: string };
  linkCard?: { title: string; rows: { label: string; value: string; warn?: boolean }[]; note?: string };
  /** "More details" second look: the first message in a modal; clicking the
   *  sender reveals `issues`. Starts the device in that modal. */
  review?: { kicker?: string; title: string; prompt: string; revealIntro: string; issues: { label: string; detail: string }[]; continueLabel?: string };
  intranet?: {
    url: string;
    siteName: string;
    navItems?: string[];
    headline?: string;
    news?: { title: string; body: string }[];
    notice: { kicker: string; title: string; body: string; watchLabel: string; dismissLabel: string };
    /** positive event recorded when the film is watched. */
    watchEventId?: string;
    video: {
      title: string;
      /** film file; omitted until the real film exists (placeholder card + spoken tip). */
      src?: string;
      poster?: string;
      tip: string;
      placeholderNote?: string;
      closeLabel?: string;
    };
  };
  /** words for the top-right guide, keyed by device stage. */
  guide?: Record<string, MfitGuideText>;
  voiceId?: string;
}

/** iPad Safari: shared-document viewer -> Microsoft sign-in -> OAuth consent. */
export interface MfitConsentConfig {
  /** "What would have happened" replay: only Accept is offered. */
  replay?: boolean;
  /** Draw the flow in a desktop browser window inside the Windows Desktop instead of the iPad. */
  frame?: "ipad" | "desktop";
  /** First stage (default "viewer"). */
  startAt?: "viewer" | "signin" | "consent";
  /** desktop frame: taskbar date. */
  date?: string;
  /** extra events recorded together with acceptEventId. */
  acceptExtraEventIds?: string[];
  /** event recorded when the app info panel is opened. */
  appInfoEventId?: string;
  /** offer "Report it to IT" next to Cancel on the consent screen itself. */
  reportOnConsent?: boolean;
  url: string;
  tabTitle?: string;
  clock?: string;
  viewer: { brand: string; fileName: string; sharedBy: string; body: string; signInLabel: string; altLabels?: string[] };
  signin: { name: string; account: string; heading?: string; pickHint?: string };
  consent: {
    appName: string;
    publisher: string;
    appDomain: string;
    unverifiedLabel?: string;
    intro: string;
    permissions: { title: string; detail: string }[];
    footer: string;
    cancelLabel: string;
    acceptLabel: string;
    appInfoLabel?: string;
    appInfo?: { title: string; rows: { label: string; value: string; warn?: boolean }[]; note?: string };
  };
  acceptedLine: string;
  acceptEventId?: string;
  cancel: { title: string; body: string; reportLabel: string; closeLabel: string; reportEventId?: string; cancelEventId?: string; reportedNote: string };
  guide?: Record<string, MfitGuideText>;
}

/** The attacker's cloud console in the hacker room (attacker POV, no scoring). */
export interface MfitCloudDashConfig {
  view: "timeline" | "access" | "denied" | "network" | "journey";
  kicker?: string;
  title: string;
  operator?: string;
  /** spoken in order; `focus` reveals/highlights the element with that id. */
  narration: { text: string; focus?: string }[];
  voiceId?: string;
  steps?: {
    id: string;
    title: string;
    body: string;
    state?: "done" | "failed" | "denied" | "success" | "pending";
    /** first entry whose events were recorded replaces state/body. */
    stateIf?: { events?: string[]; replay?: boolean; state: "done" | "failed" | "denied" | "success" | "pending"; body?: string; statusLabel?: string }[];
    statusLabel?: string;
  }[];
  /** Continue follows the first matching route's key (edges[].on);
   *  `ifReplay` matches while a "What would have happened" replay runs.
   *  A route with neither condition always matches. */
  routes?: { key: string; ifEvents?: string[]; ifReplay?: boolean }[];
  /** First matching variant is merged over this config (same match rules). */
  variants?: { ifEvents?: string[]; ifReplay?: boolean; config: Partial<Omit<MfitCloudDashConfig, "variants" | "routes">> }[];
  access?: { id: string; label: string; value: string; detail: string }[];
  findings?: { id: string; kind: "mail" | "sheet" | "doc"; title: string; meta: string; lines?: string[] }[];
  draft?: { id: string; from: string; to: string; subject: string; body: string };
  network?: { center: string; centerRole: string; nodes: { name: string; role: string; next?: boolean }[]; caption: string };
  /** "journey": a left-to-right chain (where the data went). Node ids are narration focus ids. */
  journey?: {
    nodes: { id: string; title: string; sub?: string; icon?: "file" | "cloud" | "app" | "people" | "lock"; state?: "reached" | "blocked" | "idle"; tag?: string }[];
    caption?: string;
  };
  deniedNote?: string;
  continueLabel?: string;
}

// ---------------------------------------------------------------------------
// "MFA fatigue" course devices (mfa*). Course-local, spec-driven.
// ---------------------------------------------------------------------------

/** A phone lying on a surface; `spots` are the places it can lie (Beat.mobileAt). */
export interface DeskMobileSpec {
  spots: Record<string, { position: Vec3; rotationDeg?: number }>;
  initial?: string;
}

/** Office PC for the MFA course: sign-in with number matching, the finance
 *  portal, a spreadsheet, and a step-up verification before an upload. */
export interface MfaPcConfig {
  mode: "signin" | "portal" | "sheet" | "upload" | "uploaded";
  clock?: string;
  date?: string;
  portal: { name: string; url: string };
  /** number shown on the Microsoft screen (signin / upload). */
  number?: string;
  phoneLabel?: string;
  task?: { kicker: string; title: string; body: string };
  calendar?: { title: string; body: string };
  sheet?: {
    intranetUrl: string;
    siteName: string;
    fileName: string;
    fileUrl: string;
    columns: string[];
    rows: string[][];
    fillRow: number;
    fillCol: number;
    fillValue: string;
    fillLabel: string;
  };
  info?: { kicker?: string; title: string; body: string; continueLabel?: string };
  guide?: Record<string, MfitGuideText>;
}

export interface MfaAuthRequest {
  id: string;
  app: string;
  account: string;
  location?: string;
  /** number the PC shows; empty for attacker pushes (no screen to read it from). */
  number?: string;
  timeoutS?: number;
}

/** The learner's iPhone: Microsoft Authenticator requests, an MFA bombing
 *  sequence, and the fake IT call that leads to a number-relay page. */
export interface MfaPhoneConfig {
  mode: "auth" | "bomb" | "call";
  clock?: string;
  ownerName: string;
  requests?: MfaAuthRequest[];
  denyTip?: { title: string; body: string; retryLabel: string };
  reportPrompt?: { title: string; body: string; reportLabel: string; skipLabel: string };
  call?: {
    callerName: string;
    callerSubtitle?: string;
    voiceId?: string;
    greeting: string[];
    rounds: {
      prompt?: string;
      options: {
        id: string;
        label: string;
        reply?: string[];
        comply?: boolean;
        hangup?: boolean;
        eventId?: string;
        result?: { title: string; body: string };
      }[];
    }[];
    complyLines: string[];
    /** the learner dialled this number (ringback, then the agent answers). */
    outgoing?: boolean;
    /** a comply option ends the call (reports key "comply") instead of the SMS trap. */
    endOnComply?: boolean;
  };
  /** shown under the keypad after a while without the right number (own sign-in). */
  stuckTip?: string;
  trap?: {
    smsFrom: string;
    smsText: string;
    url: string;
    pageKicker: string;
    pageTitle: string;
    pageBody: string;
    number: string;
    /** location shown on the real Authenticator request (it is not proof). */
    location?: string;
    inspect: { title: string; rows: { label: string; value: string; warn?: boolean }[]; note?: string };
    closeLabel: string;
    report?: { title: string; body: string; reportLabel: string; skipLabel: string };
  };
  /** What would have happened: the trap only, approval only, nothing scored. */
  replay?: boolean;
  /** Retry after the outcome: no events recorded. */
  practice?: boolean;
  /** outcome name -> scoring event id. */
  events?: Partial<Record<
    | "approved_legit"
    | "denied_bombing"
    | "reported_bombing"
    | "tried_number"
    | "ignored_bombing"
    | "unreported"
    | "call_verified"
    | "call_questioned"
    | "call_complied"
    | "trap_inspected"
    | "trap_rejected"
    | "trap_reported"
    | "approved_attacker",
    string
  >>;
  guide?: Record<string, MfitGuideText>;
}

// ---------------------------------------------------------------------------
// "The Fake Browser Fix" course device (clickfix). Course-local, spec-driven.
// ---------------------------------------------------------------------------

/** Office PC / bar laptop / beach iPad surfaces for ClickFix, fake CAPTCHAs
 *  and tech-support deception. The device never runs anything: "following"
 *  the page's steps is the compromise. */
export interface ClickFixConfig {
  mode: "baseline" | "captcha" | "scare" | "remote" | "ipad" | "takeover";
  clock?: string;
  date?: string;
  /** desktop toast before the browser is opened. */
  toast?: { from: string; subject: string };
  siteUrl?: string;
  siteTitle?: string;
  siteBody?: string;
  /** captcha: the fake verification brand and line. */
  verifyBrand?: string;
  verifyLine?: string;
  /** captcha: what the page put on the clipboard (shown in Run / Terminal). */
  command?: string;
  /** scare: the fullscreen lock page. */
  scareBrand?: string;
  scareTitle?: string;
  scareBody?: string;
  scarePhone?: string;
  /** scare: the spoken "security" warning (prerendered) and its voice. */
  scareVoiceLine?: string;
  scareVoice?: string;
  /** takeover: the scammer's voice and the final button. */
  takeoverVoice?: string;
  continueLabel?: string;
  /** remote: the caller's remote-access tool. */
  remoteTool?: string;
  remoteCode?: string;
  remoteAgent?: string;
  /** ipad: the beach recognition scene. */
  ipadSite?: string;
  ipadPrompt?: string;
  /** what would have happened: only the risky action is offered. */
  replay?: boolean;
  /** outcome name -> scoring event id. */
  events?: Partial<Record<"closed" | "reported" | "verified" | "ran" | "called" | "denied" | "allowed", string>>;
  /** top-right guide per `${mode}_${stage}` (or plain stage). */
  guide?: Record<string, MfitGuideText>;
}


// ---------------------------------------------------------------------------
// "One Click Too Many" course devices (oneclick). Course-local, spec-driven.
// ---------------------------------------------------------------------------

/** A colleague who messages the learner (portrait card inside the screen). */
export interface OneClickSpeaker {
  name: string;
  role?: string;
  avatarUrl?: string;
  voiceId?: string;
}

/** A file in the QuickForge data picker. */
export interface OneClickFile {
  id: "confidential" | "synthetic" | "approved";
  name: string;
  meta: string;
  icon?: "sheet" | "demo";
  details: { label: string; value: string; warn?: boolean }[];
  note?: string;
}

/** A summary card whose body is picked from the recorded events (first match). */
export interface OneClickSummaryCard {
  kicker: string;
  title: string;
  variants: { ifEvents?: string[]; tone: "bad" | "warn" | "good"; status: string; body: string }[];
}

export interface InvoicePerson {
  name: string;
  role: string;
  avatarUrl?: string;
  voiceId: string;
}

export interface InvoiceFix {
  supplier: string;
  invoiceId: string;
  po: string;
  amount: string;
  purpose: string;
  approvedSuffix: string;
  revisedSuffix: string;
  approvedBank: string;
  revisedBank: string;
  trustedNumber: string;
  revisedNumber: string;
}

/** "The Invoice That Changed" course device. One kind, several modes, rendered
 *  on the museum tablet (brief/shipment), the wall TV (film/summary) or the
 *  desk PC inside the reused Desktop (finance/payment/incident). */
/** "The 17:00 Release" kit (devices/devday): one mode per mission screen;
 *  `content` is the mission's text and answer key, typed in the kit. */
export interface DevDayConfig {
  mode: string;
  /** Monitor id s1..s8 (the beat's screenQuad is that monitor's glass). */
  screen: string;
  portrait?: boolean;
  clock?: string;
  people?: Record<string, { name: string; role: string; avatarUrl?: string; initials: string; tone: string }>;
  content: unknown;
  guide?: Record<string, MfitGuideText>;
}

export interface InvoiceConfig {
  mode: "brief" | "film" | "shipment" | "finance" | "payment" | "incident" | "summary" | "briefing" | "attackerIntro";
  /** "What would have happened" replay: only the risky action, nothing scored. */
  replay?: boolean;
  clock?: string;
  date?: string;
  tablet?: {
    kicker?: string;
    title: string;
    body?: string[];
    lines?: { label: string; value: string }[];
    cta: string;
  };
  film?: {
    kicker?: string;
    title: string;
    intro: string;
    src: string;
    poster?: string;
    watchLabel?: string;
    skipLabel?: string;
    outroLine?: string;
    outroVoiceId?: string;
  };
  finance?: {
    people: { claire: InvoicePerson; lena: InvoicePerson; accounts: InvoicePerson };
    fix: InvoiceFix;
    mailClaire: { from: string; time: string; subject: string; body: string };
    mailRevised: { from: string; time: string; subject: string; body: string; attachment: string };
    teamsNudge: string;
    replyLine: string;
    revisedCall: string;
    trustedCall: string;
    teamsReplies: { id: string; label: string; key: string; eventId?: string; needsTrusted?: boolean }[];
    inspectionIntro?: string;
    inspection?: {
      id: string;
      label: string;
      approved?: string;
      requested?: string;
      value?: string;
      tip: string;
      okFeedback: string;
      suspiciousFeedback: string;
      isChange?: boolean;
      /** Which verdict is the correct identification of this field. */
      correct?: "suspicious" | "ok";
      /** Picks the animated-SVG template in the per-item help modal. */
      helpKind?: "account" | "thread" | "number" | "amount" | "deadline" | "verified" | "sender";
      /** Two short labels for the help modal: a warning example vs a normal example. */
      help?: { warning: string; normal: string };
    }[];
  };
  briefing?: {
    kicker?: string;
    title: string;
    body?: string[];
    lines?: { label: string; value: string }[];
    cta: string;
  };
  attackerIntro?: {
    kicker?: string;
    title: string;
    lines: string[];
    voiceId?: string;
    cta?: string;
  };
  payment?: { fix: InvoiceFix };
  incident?: {
    intro: string;
    note: string;
    actions: { id: string; label: string; sub: string; priority?: boolean; eventId?: string }[];
    closingNote: string;
    continueLabel?: string;
  };
  summary?: {
    kicker?: string;
    title: string;
    rows: { q: string; variants: { ifEvents?: string[]; a: string; tone?: "good" | "warn" | "bad" }[] }[];
    closing: string;
    continueLabel?: string;
  };
  guide?: Record<string, { title: string; body: string; actionLabel?: string }>;
  events?: Record<string, string>;
}

export interface OneClickConfig {
  mode: "film" | "analytics" | "quickforge" | "sign" | "exposync" | "summary";
  /** "What would have happened" replay: only the risky path is offered, nothing is scored. */
  replay?: boolean;
  clock?: string;
  date?: string;
  jamie?: OneClickSpeaker;
  /** Jamie's voiced lines, keyed by the stage they are spoken on. */
  lines?: Record<string, string>;
  /** top-right guide, keyed by device stage. */
  guide?: Record<string, MfitGuideText>;
  /** scoring event ids, keyed by what the learner did. */
  events?: Record<string, string>;
  film?: {
    kicker?: string;
    title: string;
    intro: string;
    src: string;
    poster?: string;
    watchLabel?: string;
    skipLabel?: string;
    /** narrator line spoken after the film ends or is skipped. */
    outroLine?: string;
    outroVoiceId?: string;
    continueLabel?: string;
  };
  analytics?: { url: string; appName: string; error: string; errorDetail: string; jamieTime: string; jamieMessage: string; link: string; openLabel?: string };
  quickforge?: {
    url: string;
    prompt: string;
    files: OneClickFile[];
    appInfo: { title: string; rows: { label: string; value: string; warn?: boolean }[]; note?: string };
    approvedUrl: string;
  };
  sign?: { kicker: string; headline: string; sub: string; cta: string };
  exposync?: { url: string; headline: string; sub: string; cta: string; features: string[]; consent: MfitConsentConfig };
  summary?: {
    kicker?: string;
    title: string;
    cards: OneClickSummaryCard[];
    response: { title: string; ifEvents: string[]; steps: string[] };
    closing: string;
    continueLabel?: string;
  };
}
/** Arcade-cabinet quiz game ("Turkey Hunt"). Questions come from the
 *  scenario's quiz beat (`quizBeatId`, else the first quiz beat). */
export interface TurkeyHuntConfig {
  /** learning (default): wrong answers never end the run; arcade: 3 lives. */
  mode?: "learning" | "arcade";
  questionCount?: number;
  timerS?: number;
  quizBeatId?: string;
  title?: string;
  /** Gun-shot sample (CDN url); synth fallback when unset. */
  shotUrl?: string;
  /** Turkey call sample (CDN url); synth fallback when unset. */
  gobbleUrl?: string;
}
/** "Spot the Fraud" arcade cabinet. */
export interface SpotFraudConfig {
  title?: string;
}
/** "Cyber Invaders" arcade cabinet. */
export interface InvadersConfig {
  title?: string;
}
/** Arcade wall TV: briefing before the cabinets, verdict and debrief after. */
export interface ArcadeTvConfig {
  voice?: string;
  briefLines?: string[];
  savedLine?: string;
  lostLine?: string;
  /** Opening screen instead of the TV: narrated story + guided/free movement choice. */
  welcome?: { kicker?: string; title: string; lines: string[] };
}
/** One clickable spot in a walkable arcade (a cabinet or the wall TV). */
export interface ArcadeSpotSpec {
  /** Also the edge key the hub beat follows when this spot is clicked. */
  id: string;
  label: string;
  role?: "tv" | "game";
  gameId?: "turkey" | "fraud" | "invaders";
  /** World position of the hovering arrow. */
  arrow: [number, number, number];
  /** World anchor for the DOM label (defaults to the arrow). */
  pin?: [number, number, number];
  maxDistance?: number;
  facingDeg?: number;
  doneLabel?: string;
  finalLabel?: string;
}
export interface ArcadeTvSpec {
  center: [number, number, number];
  width: number;
  height: number;
  rotationDeg?: number;
  bezel?: number;
}
